﻿import Phaser from 'phaser';
import { Board } from '../objects/Board';
import { Hand } from '../objects/Hand';
import { ScoreBoard } from '../objects/ScoreBoard';
import { OpponentHands } from '../objects/OpponentHands';
import { createProductionGameTransport, type GameTransport, type GameChannelLike } from '../network/gameTransport';
import { setBoardUnitSize, getBoardUnitSize } from '../utils/coordinates';
import { getUIScale } from '../config/theme';
import { BOARD_RENDER } from '../config/boardRenderConfig';
import { computeSquareSceneLayout } from '../utils/sceneLayout';
import { ensureCountryFlagTextures } from '../config/countryFlags';
import { ensureAvatarTextures } from '../config/avatarTextures';
import { LAYOUT_SPEC, DESIGN_SIZE, HAND_SCALE_SPEC, HAND_SLOT_GAP_SPEC, designToCanvas } from '../config/layoutSpec';
import { IS_MOBILE_EXPERIENCE } from '../utils/deviceMode';
import { DesktopPlayerCards } from '../objects/DesktopPlayerCards';
import { DESKTOP_THEME } from '../theme/desktopTokens';
import { GameSoundManager, GAMEPLAY_SOUND_BANK } from '../audio/GameSoundManager';
import { getPlayerName } from '../utils/playerName';
import { ShuffleDealAnimator } from '../objects/ShuffleDealAnimator';
import { TILE_SKIN, TILE_SKIN_FOLDER, EXPERIMENTAL_TILE_KEYS } from '../config/tileSkin';
import { getDominoTextureKey, getDominoAssetPath, getDominoBackTextureKey, getDominoBackAssetPath, getDominoNativeHandTextureKey, getDominoNativeHandAssetPath } from '../config/dominoAssets';
import { USE_NATIVE_84X42_HAND_TILES } from '../config/handTileExperiment';
// Cosmetics: durable viewer-skin plumbing (kept when the selection source
// below changes). The skin is currently selected by the temporary rule in
// config/viewerCosmetics.ts — see that file for what is and
// isn't temporary about this wiring.
import type { ViewerTileSkin, ViewerTableSkin } from '../config/viewerCosmetics';
import { getSkinHandTextureKey, getSkinHandAssetPath, getSkinPlacedTextureKey, getSkinPlacedAssetPath, getSkinBackTextureKey, getSkinBackAssetPath } from '../config/viewerTileSkinAssets';
import { getViewerTableTextureKey, getViewerTableAssetPath } from '../config/viewerTableSkinAssets';
import { type SessionChannelHandle } from '../network/sessionChannelHub';
import { resolveSessionId } from '../network/sessionIdentity';
import { PlayerTimerRings, type PlayerTimerRingsLayout } from '../objects/PlayerTimerRings';
import { KnockButton, type KnockButtonLayout } from '../objects/KnockButton';
import { ASSETS } from '../config/assets';
import type {
  GameState,
  FullGameStateForUser,
  HandTile,
  Player,
  InitialStateMessage,
  PrivateInitialStateMessage,
  GameUpdateMessage,
  PrivateUpdateMessage,
  GameFinishedMessage,
  ErrorMessage,
  BoardLayout,
  ValidPosition,
} from '../types/game';
import type { RoundEndPayload, NextRoundReadyPayload, RoundEndExitedPayload, RematchSnapshot, RematchStatePayload, RematchResolvedPayload } from '../types/roundEnd';
import { NextRoundHandshake } from './nextRoundHandshake';
import { GAME_DIAGNOSTICS, diag } from '../debug/gameDiagnostics';
import { recordGameExit } from '../debug/exitBreadcrumbs';
import { evaluateStateSeq, shouldTriggerResumeRefresh, NO_STATE_APPLIED } from '../network/stateSequenceGate';
import { attachTutorialBoundsReporter } from '../tutorial/phaserBoundsReporter';
import { attachTutorialActionCues } from '../tutorial/tutorialActionCues';
import { t as translate, onLanguageChanged } from '../i18n';

const STARTING_HAND_SIZE = 7;

interface GameSceneData {
  gameId: number;
  sessionId: number;
  teamScores?: Record<string, number>;
  useDesktopRender?: boolean;
  /** Cosmetics: viewer-selected tile skin (durable). Selection source is the
   *  session's effective cosmetics, resolved in GameScreen. */
  viewerTileSkin?: ViewerTileSkin;
  /** Cosmetics: viewer-selected table skin (durable). */
  viewerTableSkin?: ViewerTableSkin;
  /** Network seam (see network/gameTransport.ts). Absent → the production
   *  transport (the existing singletons, unchanged). The tutorial's scripted
   *  screen and the friend-spectate screen both inject a non-default value
   *  here — see `spectate` below for how the two are told apart. */
  transport?: GameTransport;
  /** True only for the friend-spectate screen's transport (a THIN wrapper
   *  around the real production channels/REST, not a scripted/fake one —
   *  see network/gameTransport.ts createSpectatorGameTransport). Deliberately
   *  independent of `transport` being present: `transport` alone means
   *  "non-default network seam" (true for BOTH tutorial and spectator), while
   *  `spectate` disambiguates which — a spectator must get the real turn
   *  timer and none of the tutorial-only spotlight/cue presentation (see
   *  _scriptedPresentation below), while still having every input path
   *  disabled like the tutorial's read-only moments. */
  spectate?: boolean;
}

export class GameScene extends Phaser.Scene {
  // Monotonically increasing counter — each new GameScene instance gets a
  // unique id so zombie vs live instances are unambiguous in the console.
  private static _nextInstanceId = 0;
  private readonly _instanceId: number;

  // Game objects
  private board!: Board;
  private hand!: Hand;
  private scoreBoard?: ScoreBoard;
  private opponentHands?: OpponentHands;
  private desktopPlayerCards?: DesktopPlayerCards;
  private isMobile = false;
  private useDesktopRender = false;
  // Cosmetics: viewer-selected skins (durable). Resolved once in init() from
  // scene data; never reassigned mid-game. Selection source is the session's
  // effective per-viewer cosmetics (resolved in GameScreen).
  private viewerTileSkin: ViewerTileSkin = 'classic';
  private viewerTableSkin: ViewerTableSkin = 'green_fabric';

  // Network
  private gameChannel!: GameChannelLike;
  // Network seam — resolved once per init(); production transport by default.
  private transport: GameTransport = createProductionGameTransport();
  // Scripted tutorial presentation (an injected transport, MINUS spectate —
  // see GameSceneData.spectate): the turn-timer ring is not created (the
  // tutorial has no time pressure, so a countdown would mislead) and the
  // read-only spotlight bounds reporter attaches. Never true in real matches
  // OR while spectating — nothing else may branch on this.
  private _scriptedPresentation = false;
  private _tutorialBoundsDetach: (() => void) | null = null;
  // Friend-spectating (real data, real channels, just read-only). Gates
  // input wiring only — presentation (turn timer, cards, board) is left
  // completely alone, so a spectator sees exactly what the match looks like.
  // Carried forward across in-scene round transitions alongside `transport`
  // (see restartForNextRound / the round-finished handoff below) since a
  // spectator must stay a spectator into round 2, 3, ... of the same match.
  private _isSpectator = false;

  // State
  private gameId!: number;
  private sessionId!: number;
  private teamScores: Record<string, number> = {};
  private currentGameState: GameState | null = null;
  private myHand: HandTile[] = [];
  private myPosition = -1;
  private myTeam = -1;
  private myUserId = -1;
  private _wsConnected = false;
  // Set to true in cleanup() so async continuations and WS callbacks that
  // resume after the scene is torn down become no-ops instead of crashing.
  private _shutDown = false;
  // Tracks whether it was my turn on the previous applyGameState call so we
  // only fire turn_indicator once per turn transition, not on every state update.
  private _prevIsMyTurn = false;
  // Incremented on every reconnect. refreshGameState captures the value at call
  // time and discards the REST response if a newer reconnect has since occurred,
  // preventing a stale REST snapshot from overwriting a fresher WS state.
  private _reconnectSeq = 0;
  // Monotonically increasing per-scene counter. Incremented each time an
  // authoritative WS public game state (initial_state / game_update /
  // game_finished) is accepted and applied. Used by setupNetwork and
  // refreshGameState to detect whether WS delivered state while a REST
  // request was in flight, so the stale REST public board can be discarded.
  private _wsAppliedRevision = 0;
  // Monotonic server state sequence of the last ACCEPTED authoritative state.
  // Every state source passes through _gateGameState before applyGameState;
  // states with seq <= this are stale/duplicates and are rejected. Reset in
  // init() — i.e. only when (re)entering a game.
  private _lastAppliedStateSeq = NO_STATE_APPLIED;
  // Browser-resume reconciliation (visibilitychange → visible).
  private _onVisibilityChange: (() => void) | null = null;
  private _resumeRefreshInFlight = false;
  // Players captured from the last game_update/initial_state that included them.
  // game_finished game_state may omit players — this is the fallback for the overlay.
  private _lastKnownPlayers: Player[] = [];
  // Tracks board tile count from the previous applyGameState so we can detect
  // new placements (by any player) and play the tile_place sound once per tile added.

  // Shuffle/deal animation shown once per new game start.
  private _shuffleDealAnimator: ShuffleDealAnimator | null = null;
  // Prevents re-triggering the animation on resize or duplicate WS messages.
  private _hasShownDealAnimation = false;
  // Non-null while the deal animation is running: the real hand is deferred
  // so it doesn't render on top of the fake backs.  Revealed on completion.
  private _pendingHandForReveal: HandTile[] | null = null;
  // Safety fallback: reveals the real hand if the animation stalls.
  private _dealFallbackTimer: Phaser.Time.TimerEvent | null = null;

  // Travel-animation sources: tileId → scene-space origin collected just before
  // renderBoard is called.  Local tiles come from the hand position captured at
  // play time; opponent tiles get a directional origin near their player card.
  private _travelSources = new Map<number, { x: number; y: number }>();

  private _leaveMatchBtn: Phaser.GameObjects.Text | null = null;
  /** Locale-bridge unsubscribe (Phase 9) — set in init(), torn down in cleanup(). */
  private _unsubscribeLocale: (() => void) | null = null;

  // Round-end handoff state
  // True while Phaser is waiting for next_game_created after a game_finished.
  // Guards reconnect logic so a REST sync during the overlay period does not
  // send the player back to MenuScene.
  private _awaitingNextRound    = false;
  private _sessionHandle:         SessionChannelHandle | null = null;
  // Scores captured at round end — forwarded to the next GameScene on transition.
  private _lastKnownScores:       Record<string, number> = {};
  // Prevents duplicate phaser:round-end dispatches if game_finished arrives twice.
  private _roundEndDispatched   = false;
  // Ordinary-round exit handshake: defers the in-scene restart until React
  // acknowledges the overlay exit (react:round-end-exited) or its fallback
  // timer fires, so the finished board stays rendered behind the overlay for
  // the whole exit. Consume-once semantics live in nextRoundHandshake.ts.
  private readonly _nextRoundHandshake = new NextRoundHandshake();
  // Stored reference for the react:round-end-exited listener (init/cleanup pair).
  private _onRoundEndExitedAck:   ((e: Event) => void) | null = null;
  // Stored reference for the phaser:navigate-home listener so it can be
  // removed in cleanup() if the session-over overlay is dismissed via another path.
  private _onNavigateHome:        (() => void) | null = null;
  // Final-match rematch handoff state.
  // Window listener for REST vote responses forwarded by React
  // ('phaser:rematch-snapshot') so REST and WS resolutions funnel through the
  // SAME authoritative handler (_onRematchState).
  private _onRematchSnapshot:     ((e: Event) => void) | null = null;
  // Prevents double navigation when the resolved rematch state arrives more
  // than once (WS broadcast + REST response + reconnect restore).
  private _rematchHandled = false;
  // 'bots' | 'online' — captured at match end; decides the rejection route.
  private _finalGameMode: 'bots' | 'online' = 'online';

  // Mobile auto-fit play area (updated on every layout computation)
  private mobilePlayAreaCX = 0;
  private mobilePlayAreaCY = 0;
  private mobilePlayAreaW  = 0;
  private mobilePlayAreaH  = 0;

  // Vignette overlay (mobile felt effect)
  private vignetteGfx: Phaser.GameObjects.Graphics | null = null;

  // Desktop auto-fit: keep tiles inside the felt area
  private desktopFeltCX   = 0;
  private desktopFeltCY   = 0;
  private desktopFeltSize = 0;   // felt inner area is square (px)
  // Layout-spec furniture images (desktop only) — rebuilt on every resize.
  // The red hand background lives in Hand.ts (it hugs the live tile count).
  private _bgImg: Phaser.GameObjects.Image | null = null;
  private _holderImg: Phaser.GameObjects.Image | null = null;

  // Turn timer — resets on every WS game_update / initial_state turn change
  private _turnTimer:   PlayerTimerRings | null = null;
  private _knockButton: KnockButton | null = null;
  // Composite turn-change sentinel: tracks last seen (current_turn, board tiles count).
  // Using tiles count as secondary key catches the case where the backend batches bot
  // turns and sends a single game_update with the same human seat index as the previous
  // turn — current_turn alone would not detect the change.
  private _prevTurnPos    = -1;
  private _prevTilesCount = -1;
  constructor() {
    super({ key: 'GameScene' });
    this._instanceId = ++GameScene._nextInstanceId;
    console.log(`[GameScene#${this._instanceId}] constructor`);
  }

  init(data: GameSceneData): void {
    console.log(`[GameScene#${this._instanceId}] init  game=${data.gameId} session=${data.sessionId} (prev game=${this.gameId ?? 'none'})`);

    // If a previous create() was interrupted mid-flight (e.g. double scene.start),
    // tear down its channel immediately so its callbacks never fire again.
    if (this.gameChannel) {
      console.log(`[GameScene#${this._instanceId}] init: disposing stale channel for game=${this.gameId}`);
      this.gameChannel.unsubscribe();
      (this as unknown as { gameChannel: GameChannelLike | undefined }).gameChannel = undefined;
    }

    // Tear down any lingering round-end session channel from the previous game.
    if (this._sessionHandle) {
      this._sessionHandle.release();
      this._sessionHandle = null;
    }
    this._awaitingNextRound  = false;
    this._lastKnownScores    = {};
    this._roundEndDispatched = false;
    this._rematchHandled     = false;
    if (this._onNavigateHome) {
      window.removeEventListener('phaser:navigate-home', this._onNavigateHome);
      this._onNavigateHome = null;
    }
    if (this._onRematchSnapshot) {
      window.removeEventListener('phaser:rematch-snapshot', this._onRematchSnapshot);
      this._onRematchSnapshot = null;
    }

    // Ordinary-round exit handshake: drop any pending transition from the
    // previous round and (re)register the React acknowledgment listener.
    // Using remove-then-add keeps this idempotent if init() runs more than
    // once on the same instance (same discipline as the listeners above).
    this._nextRoundHandshake.cancel();
    if (this._onRoundEndExitedAck) {
      window.removeEventListener('react:round-end-exited', this._onRoundEndExitedAck);
      this._onRoundEndExitedAck = null;
    }
    this._onRoundEndExitedAck = (e: Event) => {
      const ackGameId = (e as CustomEvent<RoundEndExitedPayload>).detail?.gameId;
      if (this._shutDown || typeof ackGameId !== 'number') return;
      // The handshake ignores acks whose gameId does not match the pending
      // transition, so a stale ack can never advance a different scene.
      this._nextRoundHandshake.acknowledge(ackGameId);
    };
    window.addEventListener('react:round-end-exited', this._onRoundEndExitedAck);

    // Allow the new create() lifecycle to proceed.
    this._shutDown = false;

    // Locale bridge (Phase 9): one subscription per active scene, registered
    // with the same remove-then-add idempotency discipline as the listeners
    // above. On a runtime language switch it updates PRESENTATION only —
    // redraws the (mobile) Leave label and re-renders the seat-card names
    // from the already-applied state. It never touches game state, the
    // transport, timers, input, or state_seq.
    this._unsubscribeLocale?.();
    this._unsubscribeLocale = onLanguageChanged(() => {
      if (this._shutDown) return;
      if (this._leaveMatchBtn) this.drawLeaveMatchButton();
      if (this.currentGameState) this.refreshPlayerCards(this.currentGameState);
      // PASS/PASO: pure texture-key re-resolution — same control, same
      // bounds, same armed state and eligibility.
      this._knockButton?.refreshLocale();
    });

    // Wire cleanup() to Phaser's scene 'destroy' event. This is the event that
    // game.destroy(true) actually fires — it goes through sys.destroy() which
    // emits 'destroy' on the scene's event emitter. The scene class destroy()
    // method override is NOT called by game.destroy(), so this is the only
    // reliable hook for game-level teardown. Using off+once ensures no duplicate
    // listeners accumulate if init() is called more than once on the same instance.
    this.events.off('destroy', this.cleanup, this);
    this.events.once('destroy', this.cleanup, this);

    this.gameId = data.gameId;
    this.sessionId = data.sessionId;
    this.transport = data.transport ?? createProductionGameTransport();
    this._isSpectator = !!data.spectate;
    // A custom transport implies non-default presentation UNLESS it's the
    // spectator's — the spectator transport is real production data over
    // real channels, so it must get the real turn timer and none of the
    // tutorial-only spotlight/cue attachment below.
    this._scriptedPresentation = !!data.transport && !this._isSpectator;
    this.teamScores = data.teamScores || {};
    this.useDesktopRender = data.useDesktopRender ?? false;
    this.viewerTileSkin = data.viewerTileSkin ?? 'classic';
    this.viewerTableSkin = data.viewerTableSkin ?? 'green_fabric';
    // Reset state for scene restart
    this.currentGameState = null;
    this.myHand = [];
    this.myPosition = -1;
    this.myTeam = -1;
    this.myUserId = -1;
    this._prevIsMyTurn      = false;
    this._prevTurnPos       = -1;
    this._prevTilesCount    = -1;
    this._wsAppliedRevision = 0;
    this._lastAppliedStateSeq = NO_STATE_APPLIED;
    this._resumeRefreshInFlight = false;
    this._turnTimer?.stop();
    this._turnTimer      = null;
    this._hasShownDealAnimation = false;
    this._pendingHandForReveal = null;
    this._dealFallbackTimer = null;
  }

  preload(): void {
    // ── Domino tiles — all 28 canonical faces ───────────────
    // Keys and paths are resolved by dominoAssets.ts (single source of truth).
    for (let a = 0; a <= 6; a++) {
      for (let b = a; b <= 6; b++) {
        this.load.image(getDominoTextureKey(a, b), getDominoAssetPath(a, b));
      }
    }

    // ── Experimental skin overlay (safe, additive) ──────────
    // When TILE_SKIN is not 'default', load available experimental tiles under
    // the key "skin-tile-{a}-{b}". Original keys are always loaded above so the
    // game is never broken by a missing experimental asset.
    if (TILE_SKIN !== 'default') {
      const folder = TILE_SKIN_FOLDER[TILE_SKIN];
      for (const key of EXPERIMENTAL_TILE_KEYS) {
        this.load.image(`skin-tile-${key}`, `${folder}/${key}.png`);
      }
    }

    // ── Cosmetics: per-skin tile textures (safe, additive; durable) ─────────
    // Loaded only when this session's resolved viewer tile skin is non-classic
    // (red / black / rainbow). Classic textures above are always loaded, so a
    // missing/failed skin asset can never block startup — render call sites
    // fall back to the classic key via scene.textures.exists() guards. This
    // additive-load pattern is the reusable mechanism every skin uses.
    if (this.viewerTileSkin !== 'classic') {
      const skin = this.viewerTileSkin;
      for (let a = 0; a <= 6; a++) {
        for (let b = a; b <= 6; b++) {
          this.load.image(getSkinHandTextureKey(skin, a, b), getSkinHandAssetPath(skin, a, b));
          this.load.image(getSkinPlacedTextureKey(skin, a, b), getSkinPlacedAssetPath(skin, a, b));
        }
      }
      this.load.image(getSkinBackTextureKey(skin), getSkinBackAssetPath(skin));
    }

    // ── TEMPORARY — native 84×42 hand-tile quality experiment (safe, additive) ──
    // See src/config/handTileExperiment.ts. Loaded under a separate key
    // namespace ("domino-native-…") alongside the production keys above, so
    // this is purely additive — the production textures loaded above are
    // always present and unaffected either way.
    if (USE_NATIVE_84X42_HAND_TILES) {
      for (let a = 0; a <= 6; a++) {
        for (let b = a; b <= 6; b++) {
          this.load.image(getDominoNativeHandTextureKey(a, b), getDominoNativeHandAssetPath(a, b));
        }
      }
    }

    // ── Board & UI assets ────────────────────────────────────
    this.load.image('table',                      ASSETS.board.TABLE_FELT);
    this.load.image('felt',                       ASSETS.board.FELT_GREEN);
    this.load.image(getDominoBackTextureKey(),    getDominoBackAssetPath());
    this.load.image('desktop-table',              ASSETS.board.TABLE_CLASSIC);
    // Cosmetics: viewer-selected table skin (durable; additive). 'desktop-table'
    // above is always loaded as the fallback, so a missing table asset can never
    // block startup — Board.drawFelt() guards with textures.exists().
    this.load.image(getViewerTableTextureKey(this.viewerTableSkin), getViewerTableAssetPath(this.viewerTableSkin));
    this.load.image('knock-fist',                 ASSETS.dominoesUi.KNOCK_FIST);

    // ── Layout-spec furniture (config/layoutSpec.ts, desktop) ────────────
    this.load.image('game-bg',        '/Assets/game/layout/game-bg.png');
    this.load.image('tile-holder',    '/Assets/game/layout/tile-holder.png');
    this.load.image('team-a-bg',      '/Assets/game/layout/team-a-bg.png');
    this.load.image('team-b-bg',      '/Assets/game/layout/team-b-bg.png');
    this.load.image('pass-active',    '/Assets/game/layout/pass-active.png');
    this.load.image('pass-inactive',  '/Assets/game/layout/pass-inactive.png');
    // Spanish PASO variants — both locales are always preloaded so a runtime
    // language switch swaps the texture in place with no load stall.
    this.load.image('paso-active',    '/Assets/game/layout/paso-active.png');
    this.load.image('paso-inactive',  '/Assets/game/layout/paso-inactive.png');
    this.load.image('avatar-display-team-a', '/Assets/game/layout/avatar-display-team-a.png');
    this.load.image('avatar-display-team-b', '/Assets/game/layout/avatar-display-team-b.png');

    // Player Identity V1: avatars are NOT preloaded here. Only the ≤4
    // avatars the current game actually needs load on demand from the gated
    // game state (ensureAvatarTextures in applyGameState — same pattern as
    // country flags).

    // ── Gameplay audio bank via Phaser WebAudio ──────────────────────
    // A key that fails to load/decode simply stays out of the cache and
    // that sound falls back to HTMLAudioElement — but log it loudly.
    for (const [key, url] of GAMEPLAY_SOUND_BANK) {
      this.load.audio(key, url);
    }
    this.load.on('loaderror', (file: Phaser.Loader.File) => {
      if (file.type === 'audio') {
        console.warn(`[GameScene] audio load/decode failed for ${file.src} — falling back to HTMLAudioElement`);
      }
    });
  }

  async create(): Promise<void> {
    console.log(`[GameScene#${this._instanceId}] create start  game=${this.gameId} _shutDown=${this._shutDown}`);
    GameSoundManager.registerPhaserSound(this.sound);
    const w = this.scale.width;
    const h = this.scale.height;
    console.log(`[GameScene#${this._instanceId}] canvas size: ${w}×${h}`);

    // ── Compute layout zones and set unit size before creating Board ──
    const layout = this.computeLayout(w, h);
    this.isMobile = layout.isMobile;
    setBoardUnitSize(layout.unit);

    // true only for the mobile green-felt path; real desktop = false even though
    // useDesktopRender is also false there (useDesktopRender is only set to true
    // on a phone that explicitly wants the desktop visual render).
    const isMobileRender = this.isMobile && !this.useDesktopRender;

    // ── Background ───────────────────────────────────────────────────────
    // Mobile: solid flat green fills the entire canvas — no image, no borders,
    // tiles float on a seamless felt surface.
    // Desktop: plain black canvas (the wood table provides the background).
    this.cameras.main.setBackgroundColor(isMobileRender ? '#162b0a' : DESKTOP_THEME.color.pageBg);
    this.drawVignette(w, h, isMobileRender);

    // Store play area dimensions for autoFitToPlayArea calls.
    // On mobile the board lives in the narrower strip between the side panels.
    if (isMobileRender) {
      this.mobilePlayAreaCX = layout.boardX;
      this.mobilePlayAreaCY = layout.boardY;
      this.mobilePlayAreaW  = layout.mobilePlayW;   // width between side panels
      this.mobilePlayAreaH  = layout.mobilePlayH;
    } else {
      this.desktopFeltCX   = layout.desktopFeltCX;
      this.desktopFeltCY   = layout.desktopFeltCY;
      this.desktopFeltSize = layout.desktopFeltSize;
    }

    // ── Create game objects ──
    this.board = new Board(this, isMobileRender, this.viewerTileSkin, this.viewerTableSkin);
    this.board.setPosition(layout.boardX, layout.boardY);
    this.board.setOnTileSettle(() => GameSoundManager.playTilePlace());

    this.drawLayoutFurniture(layout);

    this.hand = new Hand(this, this.viewerTileSkin);
    this.hand.setMobile(this.isMobile);
    this.hand.setHandScale(layout.handScale);
    this.hand.setYAnchor(layout.handY);
    this.hand.setSlotSpacing(layout.handSlotSpacing > 0 ? layout.handSlotSpacing : null);
    // Desktop: the spec's holder image replaces Hand's drawn rack card.
    this.hand.setRackCardVisible(layout.layoutK <= 0);
    this.hand.setBoard(this.board);

    if (isMobileRender) {
      this.scoreBoard = new ScoreBoard(this);
      this.opponentHands = new OpponentHands(this, this.viewerTileSkin);
      this.applyOpponentHandsLayout(layout);
    } else {
      this.emitDesktopLayoutUpdate(layout.boardX, layout.boardY, layout.boardVisualSize);
      this.desktopPlayerCards = new DesktopPlayerCards(this, this.viewerTileSkin);
      this.desktopPlayerCards.setLayout(layout.boardX, layout.boardY, layout.boardVisualSize, layout.unit, layout.layoutK);
    }

    // ── Set up hand callbacks ──
    this.hand
      .onPlayTile((tileId, position, sourcePos, chosenVp, inputMethod) => this.handlePlayTile(tileId, position, sourcePos, chosenVp, inputMethod))
      .onPassAvailable((available) => this._syncKnock(available))
      .onTileSelected(() => GameSoundManager.playTileSelect());

    // ── Circular player-ring timers ──
    // Created BEFORE setupNetwork so the first applyGameState (fired from the REST
    // fetch inside setupNetwork) can call reset() on a valid instance.
    // Scripted tutorial presentation: no timer is created at all — every
    // later use is already optional-chained, so the ring simply never shows.
    if (!this._scriptedPresentation) {
      this._turnTimer = new PlayerTimerRings(this);
      this._turnTimer?.setLayout(this._timerLayout(layout));
    } else {
      // Tutorial spotlight: report real object bounds to React on request,
      // and animate the required tile's action cue (both detached in
      // cleanup(); neither exists in real matches).
      const detachCues = attachTutorialActionCues({
        tweens: this.tweens,
        getHandTileSprite: (id) => this.hand.getTileSprite(id),
      });
      const detachBounds = attachTutorialBoundsReporter({
        getHandTileBounds:        (id) => this.hand.getTileBounds(id),
        getHandRackBounds:        ()   => this.hand.getRackBounds(),
        getBoardTileBounds:       (id) => this.board.getTileSprites().get(id)?.getBounds() ?? null,
        getPlacementPreviewBounds: ()  => this.board.getPlacementPreviewBounds().map(p => p.rect),
        getSeatBounds:            (pos) => this.desktopPlayerCards?.getSeatBounds(pos) ?? null,
        getKnockBounds:           ()   => this._knockButton?.getButtonBounds() ?? null,
        getCanvas:                ()   => this.game.canvas ?? null,
      }, this.scale);
      this._tutorialBoundsDetach = () => { detachBounds(); detachCues(); };
    }

    // ── Knock button — replaces PASS; shows when local player has no legal moves ──
    this._knockButton = new KnockButton(this)
      .setOnKnock(() => this.handlePassTurn());
    this._knockButton.setLayout(this._knockLayout(layout));

    // ── Fetch initial state via REST and subscribe to WebSocket ──
    if (GAME_DIAGNOSTICS) diag.attach(this, this.board, this.game);
    await this.setupNetwork();

    // ── Leave Match button — canvas button only on mobile; desktop uses the React panel ──
    if (this.isMobile) this.drawLeaveMatchButton();

    // ── Handle resize ──
    // ScaleManager is game-level and survives scene.start() between rounds, so
    // off-before-on prevents one listener accumulating per round (each round
    // re-runs create() on the same scene instance).
    this.scale.off('resize', this._onScaleResize, this);
    this.scale.on('resize', this._onScaleResize, this);

    // ── Browser-resume reconciliation ──
    // Mobile browsers freeze/drop state while backgrounded and the server
    // keeps playing (bots, turn timer). On return to visibility, re-sync via
    // the existing guarded refreshGameState path (_reconnectSeq supersession +
    // wsRev discard + state_seq gate). One refresh at a time; no Phaser
    // recreation, no navigation, no pagehide behavior.
    if (this._onVisibilityChange) {
      document.removeEventListener('visibilitychange', this._onVisibilityChange);
    }
    this._onVisibilityChange = () => {
      if (!shouldTriggerResumeRefresh({
        visibilityState: document.visibilityState,
        shutDown:        this._shutDown,
        refreshInFlight: this._resumeRefreshInFlight,
      })) return;
      this._resumeRefreshInFlight = true;
      this._reconnectSeq++;
      console.log(`[GameScene#${this._instanceId}] visibility resume — re-syncing state  game=${this.gameId} seq=${this._reconnectSeq}`);
      this.refreshGameState(this._reconnectSeq, 'visibility_resume')
        .finally(() => { this._resumeRefreshInFlight = false; });
    };
    document.addEventListener('visibilitychange', this._onVisibilityChange);
  }

  private _onScaleResize(gameSize: Phaser.Structs.Size): void {
    this.handleResize(gameSize.width, gameSize.height);
  }

  // ─── Layout ──────────────────────────────────────────────

  /**
   * Compute vertical zones and key positions for the current viewport.
   *
   * Two paths:
   *
   *  Portrait (h > w × 1.1 — phone held vertically):
   *    Bottom-up layout.  Hand anchored near the screen bottom with enlarged
   *    tiles.  Board positioned just above the hand.  Any leftover space
   *    accumulates between the header and the board (natural breathing room),
   *    instead of below the hand (dead space).
   *
   *  Landscape / desktop:
   *    Top-down layout.  Board starts just below the header; hand sits just
   *    below the board with a small frame overlap.
   *
   *  The board display is always 18×18 units square (14-unit playable grid +
   *  2-unit decorative frame on each side).  Unit = largest integer where 18
   *  units fit in both the available width and height.
   */
  // Maximum side panel width — actual width is min(this, 22% of screen)
  static readonly SIDE_PANEL_W = 78;
  // Height of top (partner) opponent strip (mobile only)
  static readonly TOP_PANEL_H  = 56;

  private computeLayout(w: number, h: number): {
    unit: number;
    handScale: number;
    boardX: number;
    boardY: number;
    handY: number;
    /** Design→canvas factor (drawnTableSize / DESIGN_SIZE). 0 on mobile. */
    layoutK: number;
    /** Spec-driven gap between hand slots in px. 0 on mobile (Hand falls back). */
    handSlotSpacing: number;
    isMobile: boolean;
    mobilePlayW: number;
    mobilePlayH: number;
    desktopFeltCX: number;
    desktopFeltCY: number;
    desktopFeltSize: number;
    boardVisualSize: number;
    screenW: number;
    screenH: number;
    sidePanelW: number;
    topPanelH: number;
    playAreaTopY: number;
    playAreaBottomY: number;
  } {
    const isMobile   = IS_MOBILE_EXPERIENCE;  // fixed at load — never switches on resize
    const isPortrait = isMobile && !this.useDesktopRender;
    const headerH    = isPortrait ? ScoreBoard.HEADER_HEIGHT : 0;
    const s          = getUIScale(this);

    const boardX = w / 2;

    if (isPortrait) {
      // ── Mobile: big tiles + auto-fit zoom ───────────────
      // Panel width scales with screen — 22% of width, capped at max constant
      const TOP_PANEL_H  = Math.max(52, Math.min(GameScene.SIDE_PANEL_W, Math.round(w * 0.22)));
      const SIDE_PANEL_W = Math.max(44, Math.round(TOP_PANEL_H * 0.85));  // side panels ~15% narrower

      // Play area starts below header + partner strip
      const playAreaTopY     = headerH + TOP_PANEL_H;

      // Size board tiles to fit the available vertical zone (excluding a small hand reserve).
      const handReserve      = Math.max(90, Math.round(h * 0.14));
      const bZoneH           = h - playAreaTopY - handReserve;
      const rawUnit          = Math.max(20, Math.min(
        Math.round(w / 13),
        Math.round(bZoneH / 13)
      ));
      // Mobile-only: board tiles 7% smaller so the chain fits the safe zone better.
      // handScale compensates so hand tiles keep their pre-reduction visual size.
      const unit             = Math.round(rawUnit * 0.93);
      const handScale        = (rawUnit * 0.945) / unit;
      // Vertical tile: height = 2 board units × handScale
      const handTileH        = unit * 2 * handScale;

      // Board vertically centered in the play zone (excluding hand reserve).
      const mobilePlayH      = bZoneH;
      const boardY           = playAreaTopY + mobilePlayH / 2;
      const playAreaBottomY  = playAreaTopY + mobilePlayH;

      // Felt visual bottom = boardY + half the felt height (unit * 19).
      const tableHalf        = (unit * 19) / 2;
      const feltBottomY      = boardY + tableHalf;

      // Hand: rack top flush with felt bottom; tiles may overflow downward.
      // rackPadH=8 must stay in sync with Hand.ts rackPadH.
      const handY            = feltBottomY + handTileH / 2 + 8;

      // Board play width = screen minus the actual visual footprint of each side panel.
      // Footprint = SIDE_MARGIN(8) + panW(spw*0.715). Must match OpponentHands SIDE_MARGIN.
      const mobilePlayW      = w - (8 + Math.round(SIDE_PANEL_W * 0.715)) * 2;

      return {
        unit, handScale, boardX, boardY, handY, isMobile,
        layoutK: 0, handSlotSpacing: 0,
        mobilePlayW, mobilePlayH,
        desktopFeltCX: 0, desktopFeltCY: 0, desktopFeltSize: 0,
        boardVisualSize: 0,
        screenW: w, screenH: h,
        sidePanelW: SIDE_PANEL_W, topPanelH: TOP_PANEL_H,
        playAreaTopY, playAreaBottomY,
      };

    } else {
      // ── Desktop / landscape: layout-spec driven (config/layoutSpec.ts) ──
      // The square drives the table; the table drives the tile unit; every
      // other position/size comes from the 1000x1000 design spec mapped by
      // one uniform factor k. No independent caps or per-element offsets.
      const sq = computeSquareSceneLayout(w, h, BOARD_RENDER.TABLE_VISUAL_SCALE);
      const unit            = sq.unit;
      const boardVisualSize = sq.tableSize;
      const boardY          = sq.tableCY;

      // Design → canvas factor, anchored to the DRAWN table so sub-pixel
      // rounding of tableSize can never desync furniture from the table.
      const k = sq.tableSize / DESIGN_SIZE;

      // Hand: spec slot geometry. handScale converts the engine grid unit
      // into the spec's on-screen tile width (unit * handScale ≈ 81.9k),
      // so Hand.ts's existing sizing lands exactly on the design.
      const handScale       = HAND_SCALE_SPEC;
      const handTileH       = unit * 2 * handScale;
      const handSlotSpacing = Math.round(HAND_SLOT_GAP_SPEC * k);
      const specHandY = designToCanvas(LAYOUT_SPEC.hand.y, DESIGN_SIZE / 2, sq.tableCY, k);
      // Safety clamp only — spec y=885 sits well inside the square normally.
      const handY = Math.min(specHandY, h - handTileH / 2 - 16);

      // Felt inner play area — derived from the displayed table bounds rather
      // than from `unit` directly, but at the identical pre-refactor ratio
      // (12 / (19 * 1.15)), so play-area clamping behaviour is unchanged.
      const desktopFeltSize = sq.feltSize;

      // Board anchor. Already whole-pixel from computeSquareSceneLayout —
      // a fractional container position (e.g. w/2 on an odd-width canvas)
      // combined with fractional per-tile offsets rendered tile PNGs on
      // sub-pixel boundaries, which the browser resampled into a faint blur.
      // Mobile's boardX/boardY (above) are untouched.
      const desktopBoardX = sq.tableCX;
      const desktopBoardY = sq.tableCY;

      return {
        unit, handScale, boardX: desktopBoardX, boardY: desktopBoardY, handY, isMobile,
        layoutK: k, handSlotSpacing,
        mobilePlayW: 0, mobilePlayH: 0,
        desktopFeltCX: desktopBoardX, desktopFeltCY: desktopBoardY, desktopFeltSize,
        boardVisualSize,
        screenW: w, screenH: h,
        sidePanelW: 0, topPanelH: 0,
        playAreaTopY: 0, playAreaBottomY: 0,
      };
    }
  }

  // ─── Network Setup ───────────────────────────────────────

  /**
   * The REST GameState's session_id is authoritative (F4). A direct /game/:id
   * deep link may boot with sessionId=0 (no nav state / sessionStorage); adopt
   * the real id here so the round-end SessionChannel, the round-end payload's
   * sessionId, and next-round transitions all use the correct session. Idempotent.
   */
  private _adoptAuthoritativeSessionId(sessionId: number | undefined | null): void {
    const resolved = resolveSessionId(this.sessionId, sessionId);
    if (resolved !== this.sessionId) {
      console.log(`[GameScene#${this._instanceId}] adopting authoritative session_id ${resolved} (was ${this.sessionId})  game=${this.gameId}`);
      this.sessionId = resolved;
    }
  }

  private async setupNetwork(): Promise<void> {
    console.log(`[GameScene#${this._instanceId}] setupNetwork start  game=${this.gameId}`);

    // Subscribe to WebSocket first so we don't miss messages
    this.gameChannel = this.transport.createGameChannel(this.gameId, this._instanceId);
    console.log(`[GameScene#${this._instanceId}] GameChannel created  game=${this.gameId}`);

    // Capture the ordering baseline BEFORE subscribe() so that WS state applied
    // at any point during or after subscription setup is counted. The invariant:
    // any authoritative WS state applied between now and REST resolving must
    // cause the REST public board to be rejected.
    const wsRevBeforeSubscription = this._wsAppliedRevision;

    this.gameChannel
      .onInitialState((msg) => this.onInitialState(msg))
      .onPrivateInitialState((msg) => this.onPrivateInitialState(msg))
      .onGameUpdate((msg) => this.onGameUpdate(msg))
      .onPrivateUpdate((msg) => this.onPrivateUpdate(msg))
      .onGameFinished((msg) => this.onGameFinished(msg))
      .onRematchState((msg) => this._onRematchState(msg.rematch))
      .onSessionAbandoned(() => this.onSessionAbandoned())
      .onError((msg) => this.onError(msg))
      .onDisconnect(() => {
        this._wsConnected = false;
        console.log(`[GameScene] WS disconnected  game=${this.gameId}`);
      })
      .onReconnect(() => {
        if (this._shutDown) return;
        this._reconnectSeq++;
        console.log(`[GameScene] WS reconnected  game=${this.gameId} seq=${this._reconnectSeq} — re-syncing state`);
        this._wsConnected = true;
        // Lock interaction while re-syncing so no stale-state moves can be fired
        this.hand.disableInteraction();
        // Re-sync state in case a move was in flight when the connection dropped
        this.refreshGameState(this._reconnectSeq);
      })
      .onRejected(() => this.onGameChannelRejected());

    await this.gameChannel.subscribe();
    console.log(`[GameScene#${this._instanceId}] GameChannel subscribed  game=${this.gameId}`);

    // Capture revision after subscribe() returns — used by diagnostics to distinguish
    // WS-during-subscribe from WS-during-REST-await (both cases still reject REST).
    const wsRevAfterSubscription = this._wsAppliedRevision;

    // Guard: if the scene was torn down while we awaited subscribe(), abort.
    if (this._shutDown) {
      console.warn(`[GameScene#${this._instanceId}] setupNetwork: scene shut down during subscribe — aborting  game=${this.gameId}`);
      this.gameChannel.unsubscribe();
      return;
    }

    this._wsConnected = true;

    // Also fetch full game state via REST (includes private hand data)
    try {
      console.log(`[GameScene#${this._instanceId}] REST getGameState start  game=${this.gameId}`);
      const fullState = await this.transport.getGameState(this.gameId);

      // Guard: if the scene was torn down while the REST call was in flight, abort.
      if (this._shutDown) {
        console.warn(`[GameScene#${this._instanceId}] setupNetwork: scene shut down during REST fetch — aborting  game=${this.gameId}`);
        return;
      }

      // Adopt the authoritative session id before any session-scoped work (F4).
      this._adoptAuthoritativeSessionId(fullState.session_id);

      console.log(`[GameScene] REST getGameState done  game=${this.gameId}`, {
        myPosition: fullState.my_position,
        myTeam: fullState.my_team,
        currentTurn: fullState.current_turn,
        handSize: fullState.my_hand?.length,
        boardTiles: fullState.board_layout?.tiles?.length,
        status: fullState.status,
      });

      // If the game is not active: a finished round whose session continues is
      // restored into the Round End overlay (reconnect/cold-load full restore);
      // anything else (session over / aborted) returns to the menu.
      if (fullState.status === 'aborted' || fullState.status === 'finished') {
        if (this._maybeRestoreFinishedRound(fullState)) return;
        console.warn(`[GameScene] Game ${this.gameId} is ${fullState.status}. Returning to menu.`);
        recordGameExit(fullState.status === 'aborted' ? 'game_aborted' : 'session_finished', {
          gameId: this.gameId, sessionId: this.sessionId,
          wsConnected: this._wsConnected, note: 'initial-load-not-active',
        });
        this.cleanup();
        this.scene.start('MenuScene');
        return;
      }

      // If myPosition was -1 when the WS initial_state fired (subscribe() resolves
      // before the actual WS connection confirms, so the WS message often arrives
      // before this REST response), the timer was started with _activePlacement=null
      // and the ring is invisible. Reset _prevTurnPos so applyGameState below re-anchors
      // it with the now-known myPosition.
      const positionWasUnknown = this.myPosition < 0;

      this.myPosition = fullState.my_position;
      this.myTeam = fullState.my_team;

      if (positionWasUnknown) {
        this._prevTurnPos = -1;   // force timer re-anchor in applyGameState below
      }

      // Restore session-level scores from the REST response so that rejoin,
      // Home-Continue, and direct /game/:id routes all show correct scores
      // instead of the 0-0 placeholder that InitScene passes.
      if (fullState.session_team_scores && Object.keys(fullState.session_team_scores).length > 0) {
        this.teamScores = fullState.session_team_scores;
        console.log(`[GameScene#${this._instanceId}] REST: restored session scores`, this.teamScores);
      }

      const restTileCount    = fullState.board_layout?.tiles?.length ?? 0;
      const wsRevAtResponse  = this._wsAppliedRevision;
      if (GAME_DIAGNOSTICS) {
        const renderedTileCount = this.board.getTileSprites().size;
        diag.checkRestOrdering(restTileCount, renderedTileCount);
        diag.beginTransaction(
          'hydration-rest', this.gameId, fullState.game_number,
          fullState.current_turn, restTileCount, getBoardUnitSize(), performance.now(),
          undefined,
          wsRevBeforeSubscription,
          wsRevAfterSubscription,
          wsRevAtResponse,
        );
      }

      // Ordering guard: compare against the pre-subscription baseline so that WS
      // state applied during subscribe() OR during the REST await is detected.
      // This covers round transitions (0 tiles new round vs 18 tiles stale REST),
      // same-tile-count newer states, and any other state difference.
      const restIsStale = wsRevAtResponse > wsRevBeforeSubscription;

      if (restIsStale) {
        const wsTileCount = this.currentGameState?.board_layout?.tiles?.length ?? 0;
        const wsDuringSub = wsRevAfterSubscription - wsRevBeforeSubscription;
        const wsDuringRest = wsRevAtResponse - wsRevAfterSubscription;
        console.log(
          `[GameScene#${this._instanceId}] setupNetwork: WS applied` +
          ` ${wsRevAtResponse - wsRevBeforeSubscription} update(s)` +
          ` (${wsDuringSub} during subscribe, ${wsDuringRest} during REST await)` +
          ` — discarding REST public board` +
          ` (restTiles=${restTileCount}, wsTiles=${wsTileCount},` +
          ` restGN=${fullState.game_number}, wsGN=${this.currentGameState?.game_number ?? '-'})`,
        );
        if (GAME_DIAGNOSTICS) {
          diag.markRestDiscarded(
            diag.currentTxnId,
            'ws-applied-after-subscription-baseline',
            wsRevBeforeSubscription,
            wsRevAtResponse,
            restTileCount,
            wsTileCount,
            fullState.game_number,
            this.currentGameState?.game_number,
          );
        }
        // Targeted sync: board is already correct from WS.
        // Only update myPosition-dependent UI elements (scoreboard, timer, interaction).
        this._resyncPrivateUi();
      } else if (this._gateGameState(fullState, 'rest')) {
        // No WS state arrived during the REST await — REST is authoritative.
        this.applyGameState(fullState);
      } else {
        // Stale REST snapshot (older state_seq than an already-applied WS
        // state) — keep the newer board, update only myPosition-derived UI.
        this._resyncPrivateUi();
      }
      if (GAME_DIAGNOSTICS) diag.endTransaction(diag.currentTxnId);
      // Private hand data always comes from REST (not available on the public WS channel).
      this.applyHand(fullState.my_hand);
    } catch (err) {
      console.error('[GameScene] Failed to fetch initial game state:', err);
      if (this._shutDown) return;
      // If we can't get game state, go back to menu
      recordGameExit(String(err).includes('401') ? 'auth_invalid' : 'network_fetch_failed', {
        gameId: this.gameId, sessionId: this.sessionId,
        wsConnected: this._wsConnected, note: 'setupNetwork-rest-failed',
      });
      this.cleanup();
      this.scene.start('MenuScene');
    }
  }

  // ─── Reconnect Sync ──────────────────────────────────────

  /**
   * Re-fetch the full game state from REST after a WebSocket reconnect.
   * Handles the case where a move was in-flight when the connection dropped —
   * after reconnect the board and hand will reflect whatever actually happened
   * on the server, so the player can continue without being stuck.
   */
  private async refreshGameState(seq: number, source: 'reconnect' | 'visibility_resume' = 'reconnect'): Promise<void> {
    // Capture revision immediately before the REST call. The WS subscription
    // is already active at this point, so this is the correct baseline —
    // there is no subscription step to precede (unlike setupNetwork).
    const wsRevBeforeRest = this._wsAppliedRevision;
    console.log(`[GameScene] refreshGameState start  game=${this.gameId} seq=${seq}`);
    try {
      const fullState = await this.transport.getGameState(this.gameId);

      // A newer reconnect has already triggered its own refresh — discard this
      // stale REST response so it cannot overwrite a fresher WS state update.
      if (seq !== this._reconnectSeq) {
        console.log(`[GameScene] refreshGameState: seq ${seq} superseded by ${this._reconnectSeq} — discarding`);
        return;
      }

      if (this._shutDown) {
        console.warn(`[GameScene] refreshGameState: scene shut down during REST — aborting  game=${this.gameId}`);
        return;
      }

      // Deep-link recovery: adopt the authoritative session id here too (F4).
      this._adoptAuthoritativeSessionId(fullState.session_id);

      if (fullState.status === 'aborted' || fullState.status === 'finished') {
        if (this._awaitingNextRound) {
          // Heal a missed next_game_created: the authoritative state already
          // names an active successor — route it through the SAME guarded
          // transition the live broadcast uses (duplicate/stale protection
          // included), instead of waiting for a broadcast that already fired.
          if (fullState.next_game_id && fullState.next_game_id !== this.gameId) {
            console.log(`[GameScene] refreshGameState: missed next_game_created — healing to game=${fullState.next_game_id}`);
            this._onNextGameCreated(fullState.next_game_id, fullState.session_team_scores);
            return;
          }
          // Game is finished and we are correctly waiting for next_game_created.
          // Do not navigate to menu — the session channel will trigger the transition.
          console.log(`[GameScene] refreshGameState: game finished but _awaitingNextRound=true — staying`);
          return;
        }
        // Not yet awaiting (e.g. WS dropped before the overlay opened, or a cold
        // reconnect) — restore the round-end overlay from authoritative state.
        if (this._maybeRestoreFinishedRound(fullState)) return;
        console.warn(`[GameScene] Game ${this.gameId} is ${fullState.status} after reconnect. Returning to menu.`);
        recordGameExit(fullState.status === 'aborted' ? 'game_aborted' : 'session_finished', {
          gameId: this.gameId, sessionId: this.sessionId,
          wsConnected: this._wsConnected, note: 'reconnect-refresh-not-active',
        });
        this.cleanup();
        this.scene.start('MenuScene');
        return;
      }

      if (fullState.session_team_scores && Object.keys(fullState.session_team_scores).length > 0) {
        this.teamScores = fullState.session_team_scores;
        console.log(`[GameScene] refreshGameState: restored session scores`, this.teamScores);
      }
      const restTileCount   = fullState.board_layout?.tiles?.length ?? 0;
      const wsRevAtResponse = this._wsAppliedRevision;
      if (GAME_DIAGNOSTICS) {
        const renderedTileCount = this.board.getTileSprites().size;
        diag.checkRestOrdering(restTileCount, renderedTileCount);
        // No subscription step for reconnect — wsRevBeforeSubscription and
        // wsRevAfterSubscription are both the pre-REST baseline.
        diag.beginTransaction(
          'reconnect-rest', this.gameId, fullState.game_number,
          fullState.current_turn, restTileCount, getBoardUnitSize(), performance.now(),
          undefined,
          wsRevBeforeRest,   // wsRevBeforeSubscription (reused as pre-REST baseline)
          wsRevBeforeRest,   // wsRevAfterSubscription (same — no subscribe step)
          wsRevAtResponse,
        );
      }

      // Ordering guard: same principle as setupNetwork. The existing _reconnectSeq
      // check above handles a NEWER reconnect superseding this one; this check
      // handles WS updates arriving during THIS reconnect's REST await.
      const restIsStale = wsRevAtResponse > wsRevBeforeRest;

      if (restIsStale) {
        const wsTileCount = this.currentGameState?.board_layout?.tiles?.length ?? 0;
        console.log(
          `[GameScene] refreshGameState: WS applied` +
          ` ${wsRevAtResponse - wsRevBeforeRest} update(s) during REST await` +
          ` — discarding REST public board` +
          ` (restTiles=${restTileCount}, wsTiles=${wsTileCount},` +
          ` restGN=${fullState.game_number}, wsGN=${this.currentGameState?.game_number ?? '-'})`,
        );
        if (GAME_DIAGNOSTICS) {
          diag.markRestDiscarded(
            diag.currentTxnId,
            'ws-applied-during-reconnect-rest-await',
            wsRevBeforeRest,
            wsRevAtResponse,
            restTileCount,
            wsTileCount,
            fullState.game_number,
            this.currentGameState?.game_number,
          );
        }
        this._resyncPrivateUi();
      } else if (this._gateGameState(fullState, source)) {
        this.applyGameState(fullState);
      } else {
        // Stale refresh snapshot — a newer WS state is already applied.
        this._resyncPrivateUi();
      }
      if (GAME_DIAGNOSTICS) diag.endTransaction(diag.currentTxnId);
      this.applyHand(fullState.my_hand);
    } catch (err) {
      console.error('[GameScene] refreshGameState failed:', err);
      // Non-fatal — the player can still try to play; WS updates will arrive
    }
  }

  // ─── WebSocket Message Handlers ──────────────────────────

  private onInitialState(msg: InitialStateMessage): void {
    console.log(`[GameScene#${this._instanceId}] WS initial_state  game=${this.gameId} turn=${msg.game_state.current_turn} _shutDown=${this._shutDown}`);
    if (this._shutDown) return;
    // Streaming starts before the channel's snapshot is built, so a bot-move
    // game_update can legitimately arrive first — never let this older
    // snapshot overwrite it.
    if (!this._gateGameState(msg.game_state, 'initial_state')) return;
    if (GAME_DIAGNOSTICS) {
      const tileCount = msg.game_state.board_layout?.tiles?.length ?? 0;
      diag.recordWsState(tileCount);
      diag.beginTransaction('ws-initial-state', this.gameId, msg.game_state.game_number,
        msg.game_state.current_turn, tileCount, getBoardUnitSize(), performance.now());
    }
    this.applyGameState(msg.game_state);
    this._wsAppliedRevision++;
    if (GAME_DIAGNOSTICS) diag.endTransaction(diag.currentTxnId);
  }

  private onPrivateInitialState(msg: PrivateInitialStateMessage): void {
    console.log(`[GameScene#${this._instanceId}] WS private_initial_state  game=${this.gameId} hand=${msg.my_hand?.length} _shutDown=${this._shutDown}`);
    if (this._shutDown) return;
    this.applyHand(msg.my_hand);
  }

  private onGameUpdate(msg: GameUpdateMessage): void {
    console.log(`[GameScene#${this._instanceId}] WS game_update  game=${this.gameId} turn=${msg.game_state.current_turn} _shutDown=${this._shutDown}`);
    if (this._shutDown) return;
    if (!this._gateGameState(msg.game_state, 'game_update')) return;
    if (GAME_DIAGNOSTICS) {
      const newCount  = msg.game_state.board_layout?.tiles?.length ?? 0;
      const prevCount = this.currentGameState?.board_layout?.tiles?.length ?? 0;
      diag.recordWsState(newCount);
      diag.recordWsUpdate();
      diag.checkTileCountDecreased(prevCount, newCount, 'ws-game-update');
      diag.beginTransaction('ws-game-update', this.gameId, msg.game_state.game_number,
        msg.game_state.current_turn, newCount, getBoardUnitSize(), performance.now());
    }
    // applyGameState already renders board_layout from msg.game_state.board_layout.
    // Do NOT also call renderBoardFromLayout(msg.board_layout) here — both fields
    // carry identical tile data, so the double call destroys and recreates every
    // tile sprite on every update, accumulating dead Phaser objects inside the
    // Board container until its scene reference becomes invalid.
    this.applyGameState(msg.game_state);
    this._wsAppliedRevision++;
    if (GAME_DIAGNOSTICS) diag.endTransaction(diag.currentTxnId);
  }

  private onPrivateUpdate(msg: PrivateUpdateMessage): void {
    console.log(`[GameScene#${this._instanceId}] WS private_update  game=${this.gameId} hand=${msg.my_hand?.length} _shutDown=${this._shutDown}`);
    if (this._shutDown) return;
    this.applyHand(msg.my_hand);
  }

  private onGameFinished(msg: GameFinishedMessage): void {
    console.log(`[GameScene#${this._instanceId}] WS game_finished  game=${this.gameId} session_over=${msg.session_over} scores=${JSON.stringify(msg.team_scores)} _shutDown=${this._shutDown}`);
    if (this._shutDown) return;
    if (!this._gateGameState(msg.game_state, 'game_finished')) return;

    if (GAME_DIAGNOSTICS) {
      const tileCount = msg.game_state.board_layout?.tiles?.length ?? 0;
      diag.beginTransaction('ws-game-finished', this.gameId, msg.game_state.game_number,
        msg.game_state.current_turn, tileCount, getBoardUnitSize(), performance.now());
    }
    this.applyGameState(msg.game_state);
    this._wsAppliedRevision++;
    if (GAME_DIAGNOSTICS) diag.endTransaction(diag.currentTxnId);
    this._pulseBoard();

    // Freeze gameplay interaction immediately — board is read-only from here.
    this.hand.disableInteraction();

    // Stop the turn timer — game is over, no more turns.
    this._turnTimer?.stop();

    const scores = msg.team_scores || this.teamScores;

    // Close the round-gate blind window: subscribe for next_game_created the
    // moment an ordinary finish is known, BEFORE the 1.5 s presentation delay,
    // so a broadcast landing inside that window is never missed. The later
    // call from _enterRoundEndState is idempotent on the held handle. If the
    // server advances during the delay, _onNextGameCreated transitions
    // immediately (no overlay was dispatched yet) and the scene restart
    // clears this pending delayedCall.
    if (!(msg.session_over ?? false)) this._awaitNextRound(scores);

    // Brief pause so the board's final state is visible before the overlay appears.
    this.time.delayedCall(1500, () => {
      if (this._shutDown) return;
      this._enterRoundEndState(msg, scores);
    });
  }

  /**
   * Called 1 500 ms after game_finished.
   * Dispatches the round-end payload to React and — for round-over (not
   * session-over) — subscribes to the SessionChannel to detect next_game_created.
   *
   * GameScene stays alive so Phaser can execute the next-round transition once
   * the server creates the new game; React owns the UI overlay.
   */
  private _enterRoundEndState(msg: GameFinishedMessage, scores: Record<string, number>): void {
    // Guard: game_finished can theoretically arrive twice over a flaky connection.
    if (this._roundEndDispatched) {
      console.warn(`[GameScene#${this._instanceId}] _enterRoundEndState: duplicate call ignored  game=${this.gameId}`);
      return;
    }
    this._roundEndDispatched = true;

    const sessionOver = msg.session_over ?? false;
    const gs          = msg.game_state;

    // Play the appropriate result sound (previously played inside GameOverScene).
    if (sessionOver) {
      GameSoundManager.playSessionEnd(gs.winner_team === this.myTeam);
    } else {
      GameSoundManager.playGameResult(gs.winner_team === this.myTeam);
    }

    const payload: RoundEndPayload = {
      roundNumber:        gs.game_number,
      winnerTeam:         gs.winner_team,
      winType:            gs.game_end_type,
      teamScores:         scores,
      previousTeamScores: this.teamScores,
      // game_finished game_state may omit players — fall back to last known list
      // captured from game_update / initial_state messages.
      players:            gs.players?.length > 0 ? gs.players : this._lastKnownPlayers,
      handCounts:         gs.hand_counts ?? {},
      revealedPlayers:    msg.revealed_players ?? [],
      revealedHands:      msg.revealed_hands   ?? {},
      gameId:             this.gameId,
      sessionId:          this.sessionId,
      sessionOver,
      sessionWinnerTeam:  msg.session_winner_team ?? null,
      targetScore:        msg.target_score ?? 100,
      myPosition:         this.myPosition,
      myTeam:             this.myTeam,
      // Round-end ready gate: the server deadline drives the display-only
      // countdown, and readyPositions restores this viewer's Ready ✓ on
      // reconnect. Absent on older backends → overlay falls back to a local
      // display countdown and no restored readiness.
      nextRoundDeadlineAt: gs.next_round_deadline_at ?? null,
      readyPositions:      gs.next_round_ready?.ready_positions ?? [],
      myUserId:            this._myUserId(),
      rematch:             sessionOver ? (msg.rematch ?? null) : null,
      gameMode:            gs.game_mode === 'bots' ? 'bots' : 'online',
    };

    console.log(`[GameScene#${this._instanceId}] dispatch phaser:round-end  game=${this.gameId} session_over=${sessionOver}`);
    console.log(`[round-end] players=${payload.players.length} handCounts=${Object.keys(payload.handCounts).length} revealedPlayers=${payload.revealedPlayers.length}`);
    window.dispatchEvent(new CustomEvent<RoundEndPayload>('phaser:round-end', { detail: payload }));

    if (sessionOver) {
      this._finalGameMode = gs.game_mode === 'bots' ? 'bots' : 'online';
      // Rematch flow (natural target-score completion): wait for the
      // authoritative vote resolution. Falls back to the legacy Back Home
      // relay when the backend sent no rematch snapshot (older backend or
      // non-completed terminal).
      this._enterRematchWait();
      return;
    }

    // Round over (session continues) — subscribe to SessionChannel so Phaser can
    // transition to the next game when next_game_created arrives from the server.
    this._awaitNextRound(scores);
  }

  /** The viewer's user id, resolved from the last known player list. */
  private _myUserId(): number {
    return this._lastKnownPlayers.find((p) => p.position === this.myPosition)?.id ?? 0;
  }

  /**
   * Final-match wait state: the session is over and the overlay is showing.
   * Registers the legacy Back Home relay (fallback for non-completed
   * terminals / older backends) and the REST-response funnel so vote
   * responses submitted by React resolve through the SAME authoritative
   * handler as WS rematch_state broadcasts (which arrive on the still-open
   * GameChannel — see onRematchState registration in setupNetwork).
   */
  private _enterRematchWait(): void {
    // Legacy relay: React dispatches phaser:navigate-home from the fallback
    // Back Home button (shown when no rematch vote exists).
    if (!this._onNavigateHome) {
      this._onNavigateHome = () => {
        window.removeEventListener('phaser:navigate-home', this._onNavigateHome!);
        this._onNavigateHome = null;
        if (!this._shutDown) {
          recordGameExit('session_finished', {
            gameId: this.gameId, sessionId: this.sessionId,
            wsConnected: this._wsConnected, note: 'session-over-back-home',
          });
          this.cleanup();
          this.scene.start('MenuScene');
        }
      };
      window.addEventListener('phaser:navigate-home', this._onNavigateHome);
    }

    // REST vote responses (from the overlay's submit) → same handler as WS.
    if (!this._onRematchSnapshot) {
      this._onRematchSnapshot = (e: Event) => {
        const snapshot = (e as CustomEvent<{ rematch: RematchSnapshot | null }>).detail?.rematch;
        if (snapshot) this._onRematchState(snapshot);
      };
      window.addEventListener('phaser:rematch-snapshot', this._onRematchSnapshot);
    }
  }

  /**
   * THE single authoritative rematch handler. Every source funnels here (WS
   * rematch_state on the game channel, REST vote responses via
   * phaser:rematch-snapshot, reconnect restore). Forwards the snapshot to the
   * overlay and performs at most ONE resolution transition.
   */
  private _onRematchState(snapshot: RematchSnapshot): void {
    if (this._shutDown) return;

    // Overlay re-render — display only, never navigation.
    window.dispatchEvent(new CustomEvent<RematchStatePayload>('phaser:rematch-state', {
      detail: { rematch: snapshot },
    }));

    if (snapshot.state === 'accepted' && snapshot.new_game_id && snapshot.new_session_id) {
      this._transitionToRematch(snapshot.new_game_id, snapshot.new_session_id);
    } else if (snapshot.state === 'rejected') {
      this._resolveRematchRejected();
    }
  }

  /** Accepted rematch: transition Phaser in place to the successor game —
   *  same proven mechanism as the round transition, plus a session switch. */
  private _transitionToRematch(newGameId: number, newSessionId: number): void {
    if (this._shutDown || this._rematchHandled) return;
    this._rematchHandled = true;

    console.log(`[GameScene#${this._instanceId}] rematch accepted — transitioning game=${newGameId} session=${newSessionId}`);
    // Dismiss the overlay + sync URL/sessionStorage (React listener).
    window.dispatchEvent(new CustomEvent<NextRoundReadyPayload>('phaser:next-round-ready', {
      detail: { gameId: newGameId, sessionId: newSessionId },
    }));

    this.cleanup();
    this.scene.start('GameScene', {
      gameId:           newGameId,
      sessionId:        newSessionId,
      teamScores:       {},
      useDesktopRender: this.useDesktopRender,
      viewerTileSkin:   this.viewerTileSkin,
      viewerTableSkin:  this.viewerTableSkin,
      // Seam continuity: a non-default transport survives in-scene
      // transitions ONLY for the tutorial (scripted) and the spectator (real
      // data, but a non-participant's REST/no-op transport) — both must keep
      // using their own transport into round 2. Production forwards nothing,
      // so init() builds a fresh default production transport exactly as
      // before the seam.
      transport:        (this._scriptedPresentation || this._isSpectator) ? this.transport : undefined,
      spectate:         this._isSpectator,
    });
  }

  /** Rejected rematch: React performs the navigation (single reliable hop) —
   *  online tables return to /play/online, bot matches to /play/bots. */
  private _resolveRematchRejected(): void {
    if (this._shutDown || this._rematchHandled) return;
    this._rematchHandled = true;

    const route = this._finalGameMode === 'bots' ? '/play/bots' : '/play/online';
    console.log(`[GameScene#${this._instanceId}] rematch rejected — routing everyone to ${route}`);
    recordGameExit('session_finished', {
      gameId: this.gameId, sessionId: this.sessionId,
      wsConnected: this._wsConnected, note: 'rematch-rejected',
    });
    window.dispatchEvent(new CustomEvent<RematchResolvedPayload>('phaser:rematch-resolved', {
      detail: { decision: 'rejected', route },
    }));
    // React navigation unmounts GameScreen, which destroys the Phaser game.
    // cleanup() here releases channels immediately; no scene.start — the
    // legacy MenuScene relay would race the navigation toward /home.
    this.cleanup();
  }

  /**
   * Subscribe to the session channel and wait for the server-authoritative
   * next_game_created. Shared by the live round-end path (_enterRoundEndState)
   * and the reconnect/cold-load restore path (_restoreRoundEndFromState) so both
   * transition through the SAME mechanism.
   */
  private _awaitNextRound(scores: Record<string, number>): void {
    this._awaitingNextRound = true;
    this._lastKnownScores   = scores;

    // Already acquired (early-acquire in onGameFinished) — never double-acquire
    // or re-register listeners on the shared handle.
    if (this._sessionHandle) return;

    // Acquire the shared per-session channel (F7). If React chat already holds
    // it, this reuses the same subscription instead of opening a duplicate.
    // Guard on a resolved session id: setupNetwork's REST fetch heals a deep-link
    // seed of 0 long before a round can end, but never open SessionChannel(0).
    if (this.sessionId <= 0) {
      console.error(`[GameScene#${this._instanceId}] cannot await next round — session id unresolved (0)  game=${this.gameId}`);
      return;
    }
    this._sessionHandle = this.transport.acquireSessionChannel(this.sessionId);
    this._sessionHandle.onNextGameCreated((nextMsg) => {
      console.log(
        `[DIAG:GameScene] next_game_created game=${nextMsg.game_id}` +
        ` session=${this.sessionId} _awaitingNextRound=${this._awaitingNextRound}` +
        ` _shutDown=${this._shutDown}`,
      );
      this._onNextGameCreated(nextMsg.game_id, nextMsg.team_scores);
    });
    // If the cable drops during the wait, a next_game_created may have been
    // missed — re-sync through the guarded refresh path on reconnect;
    // refreshGameState heals from the authoritative next_game_id.
    this._sessionHandle.onReconnect(() => {
      if (this._shutDown || !this._awaitingNextRound) return;
      this._reconnectSeq++;
      console.log(`[GameScene#${this._instanceId}] session channel reconnected during round-end wait — re-syncing  game=${this.gameId} seq=${this._reconnectSeq}`);
      void this.refreshGameState(this._reconnectSeq);
    });
  }

  /**
   * Reconnect / cold-load restore: this scene loaded (or re-fetched) a game that
   * is already FINISHED while the session continues. Rebuilds the Round End
   * overlay from authoritative state — restoring this viewer's Ready ✓ and the
   * server-synced countdown — and waits for next_game_created. If the round has
   * already advanced (an active successor exists), jumps straight to it instead.
   *
   * Returns true when it took over (caller must stop); false when this is NOT a
   * restorable round-end (session over / aborted → caller does its menu path).
   */
  private _maybeRestoreFinishedRound(fullState: FullGameStateForUser): boolean {
    if (fullState.status !== 'finished') return false;

    // Final-match restore: the SESSION completed naturally (target score).
    // A refresh/reconnect during the final screen must restore the completed
    // state, the viewer's vote, and any resolved outcome — never return the
    // user to active gameplay or silently bounce them to the menu.
    if (fullState.session_over && fullState.session_completion_reason === 'completed') {
      return this._restoreCompletedMatch(fullState);
    }

    // next_round_ready is non-null ONLY for a finished round whose session still
    // continues. Null → session finished/aborted → not our case.
    if (!fullState.next_round_ready) return false;

    // Already advanced while we were away → follow the live round directly.
    if (fullState.next_game_id && fullState.next_game_id !== this.gameId) {
      console.log(`[GameScene#${this._instanceId}] restore: round already advanced — jumping to game=${fullState.next_game_id}`);
      this._awaitingNextRound = true; // satisfy _onNextGameCreated's guard
      this._onNextGameCreated(fullState.next_game_id, fullState.session_team_scores);
      return true;
    }

    // Guard: don't double-dispatch if we somehow already entered round-end.
    if (this._roundEndDispatched) return true;
    this._roundEndDispatched = true;

    this.myPosition = fullState.my_position;
    this.myTeam     = fullState.my_team;
    const scores = (fullState.session_team_scores && Object.keys(fullState.session_team_scores).length > 0)
      ? fullState.session_team_scores
      : this.teamScores;

    // Freeze interaction — the board is read-only in round-end.
    this.hand.disableInteraction();
    this._turnTimer?.stop();

    const payload: RoundEndPayload = {
      roundNumber:        fullState.game_number,
      winnerTeam:         fullState.winner_team,
      winType:            fullState.game_end_type,
      teamScores:         scores,
      // On restore we lack the pre-round scores; showing no delta is the safe
      // degradation (the authoritative cumulative scores are still correct).
      previousTeamScores: scores,
      players:            fullState.players?.length > 0 ? fullState.players : this._lastKnownPlayers,
      handCounts:         fullState.hand_counts ?? {},
      // Reveals are never sent on reconnect (hand-secrecy rule); the overlay
      // falls back to hand counts.
      revealedPlayers:    [],
      revealedHands:      {},
      gameId:             this.gameId,
      sessionId:          this.sessionId,
      sessionOver:        false,
      sessionWinnerTeam:  null,
      targetScore:        fullState.target_score ?? 100,
      myPosition:         this.myPosition,
      myTeam:             this.myTeam,
      nextRoundDeadlineAt: fullState.next_round_deadline_at ?? null,
      readyPositions:      fullState.next_round_ready.ready_positions ?? [],
    };

    console.log(`[GameScene#${this._instanceId}] restore: re-entering round-end  game=${this.gameId} ready=${JSON.stringify(payload.readyPositions)}`);
    window.dispatchEvent(new CustomEvent<RoundEndPayload>('phaser:round-end', { detail: payload }));
    this._awaitNextRound(scores);
    return true;
  }

  /**
   * Reconnect / cold-load restore for a COMPLETED match (natural target-score
   * finish). Re-presents the final screen with the authoritative rematch vote
   * state; a resolved vote follows its outcome immediately (accepted → the
   * rematch game, rejected → the correct play route). Returns true always —
   * a completed match never falls through to the legacy menu bounce.
   */
  private _restoreCompletedMatch(fullState: FullGameStateForUser): boolean {
    this.myPosition = fullState.my_position;
    this.myTeam     = fullState.my_team;
    if (fullState.players?.length > 0) this._lastKnownPlayers = fullState.players;
    this._finalGameMode = fullState.game_mode === 'bots' ? 'bots' : 'online';

    const rematch = (fullState.rematch ?? null) as RematchSnapshot | null;

    // Already resolved while we were away — follow the outcome directly.
    if (rematch?.state === 'accepted' && rematch.new_game_id && rematch.new_session_id) {
      this._transitionToRematch(rematch.new_game_id, rematch.new_session_id);
      return true;
    }
    if (rematch?.state === 'rejected') {
      this._resolveRematchRejected();
      return true;
    }

    if (this._roundEndDispatched) return true;
    this._roundEndDispatched = true;

    // Freeze interaction — the board is read-only on the final screen.
    this.hand.disableInteraction();
    this._turnTimer?.stop();

    const scores = (fullState.session_team_scores && Object.keys(fullState.session_team_scores).length > 0)
      ? fullState.session_team_scores
      : this.teamScores;

    const payload: RoundEndPayload = {
      roundNumber:        fullState.game_number,
      winnerTeam:         fullState.winner_team,
      winType:            fullState.game_end_type,
      teamScores:         scores,
      previousTeamScores: scores, // no delta on restore — safe degradation
      players:            fullState.players?.length > 0 ? fullState.players : this._lastKnownPlayers,
      handCounts:         fullState.hand_counts ?? {},
      revealedPlayers:    [], // reveals are never re-sent (hand-secrecy rule)
      revealedHands:      {},
      gameId:             this.gameId,
      sessionId:          this.sessionId,
      sessionOver:        true,
      sessionWinnerTeam:  fullState.session_winner_team ?? null,
      targetScore:        fullState.target_score ?? 100,
      myPosition:         this.myPosition,
      myTeam:             this.myTeam,
      myUserId:           this._myUserId(),
      rematch:            rematch,
      gameMode:           this._finalGameMode,
    };

    console.log(`[GameScene#${this._instanceId}] restore: re-entering FINAL screen  game=${this.gameId} rematch=${rematch?.state ?? 'none'}`);
    window.dispatchEvent(new CustomEvent<RoundEndPayload>('phaser:round-end', { detail: payload }));
    this._enterRematchWait();
    return true;
  }

  /**
   * Fires when the server broadcasts next_game_created on the session channel
   * (or the refreshGameState heal path finds an existing successor).
   *
   * Ordinary path: announces the exit to React (phaser:next-round-ready) and
   * defers the in-scene restart until React acknowledges the overlay exit
   * (react:round-end-exited) or the handshake's fallback timer fires — the
   * finished board stays rendered for the whole exit. Phase 1's React ack is
   * synchronous, so the restart still happens in the same event turn.
   *
   * Jump-ahead path (reconnect into an already-advanced round, no overlay was
   * ever dispatched): transitions immediately without waiting for any ack.
   */
  private _onNextGameCreated(gameId: number, teamScores?: Record<string, number>): void {
    if (this._shutDown || !this._awaitingNextRound) return;

    this._sessionHandle?.release();
    this._sessionHandle     = null;
    this._awaitingNextRound = false;

    const scores = teamScores ?? this._lastKnownScores;

    if (!this._roundEndDispatched) {
      // Reconnect jump-ahead — no overlay is showing, nothing to exit.
      this._startNextGame(gameId, scores);
      return;
    }

    this._nextRoundHandshake.begin(
      gameId,
      () => {
        // Tell React to exit the round-end overlay. The ordinary-round hook
        // acknowledges with react:round-end-exited (echoing this gameId);
        // the final-match path never uses this handshake.
        window.dispatchEvent(
          new CustomEvent<NextRoundReadyPayload>('phaser:next-round-ready', { detail: { gameId } }),
        );
      },
      () => this._startNextGame(gameId, scores),
    );
  }

  /**
   * The actual in-scene restart. Runs exactly once per transition — via the
   * handshake (ack or fallback) on the ordinary path, or directly on the
   * reconnect jump-ahead path. Never destroys the Phaser game.
   */
  private _startNextGame(gameId: number, teamScores: Record<string, number>): void {
    if (this._shutDown) return;
    console.log(`[GameScene#${this._instanceId}] next round ready — transitioning to game=${gameId}`);
    this.cleanup();
    this.scene.start('GameScene', {
      gameId,
      sessionId:        this.sessionId,
      teamScores,
      useDesktopRender: this.useDesktopRender,
      // Cosmetics: durable — carry the resolved viewer skins across the
      // internal round transition too, not just the initial boot. Without
      // this, the skins would silently drop back to defaults on the second
      // and later rounds of a multi-round session.
      viewerTileSkin:   this.viewerTileSkin,
      viewerTableSkin:  this.viewerTableSkin,
      // Seam continuity: a non-default transport survives round transitions
      // ONLY for the tutorial (scripted) and the spectator (real data, a
      // non-participant transport) — see the identical comment at the other
      // scene.start('GameScene', ...) call site above.
      transport:        (this._scriptedPresentation || this._isSpectator) ? this.transport : undefined,
      spectate:         this._isSpectator,
    });
  }

  private onError(msg: ErrorMessage): void {
    console.error('[GameScene] Error:', msg.message);
    if (this.isMobile && !this.useDesktopRender) {
      this.scoreBoard?.showMessage(`Error: ${msg.message}`, 3000);
    }
    if (this.isMyTurn()) {
      this.hand.enableInteraction();
    }
  }

  /**
   * The server REJECTED the GameChannel subscription (membership/auth failure).
   * Without this the board would hydrate once from REST and then silently never
   * receive updates — a frozen board with no feedback (F5). Route to the same
   * safe recovery as an inactive game: tear down and return to the menu, where
   * the active-game banner offers a clean rejoin. Uses the EXISTING recovery
   * presentation (menu + banner) rather than inventing a new error screen.
   */
  private onGameChannelRejected(): void {
    if (this._shutDown) return;
    console.error(`[GameScene#${this._instanceId}] GameChannel REJECTED — returning to menu for safe recovery  game=${this.gameId}`);
    recordGameExit('game_channel_rejected', {
      gameId: this.gameId, sessionId: this.sessionId,
      wsConnected: this._wsConnected, note: 'ws-subscription-rejected',
    });
    this.cleanup();
    this.scene.start('MenuScene');
  }

  private onSessionAbandoned(): void {
    if (this._shutDown) return;
    console.log(`[GameScene] session_abandoned — navigating home`);
    recordGameExit('session_abandoned', {
      gameId: this.gameId, sessionId: this.sessionId,
      wsConnected: this._wsConnected, note: 'ws-session-abandoned-broadcast',
    });
    this.cleanup();
    this.scene.start('MenuScene');
  }

  // ─── Leave Match ──────────────────────────────────────────

  private drawLeaveMatchButton(): void {
    if (this._leaveMatchBtn) {
      this._leaveMatchBtn.destroy();
      this._leaveMatchBtn = null;
    }
    const { width } = this.scale;
    this._leaveMatchBtn = this.add.text(width - 8, 8, String(translate('gameplay:leaveButton')), {
      fontSize:   '11px',
      color:      '#ff6b6b',
      fontFamily: 'Arial, sans-serif',
      fontStyle:  'normal',
    })
      .setOrigin(1, 0)
      .setAlpha(0.6)
      .setInteractive({ useHandCursor: true })
      .on('pointerover', () => this._leaveMatchBtn?.setAlpha(1))
      .on('pointerout',  () => this._leaveMatchBtn?.setAlpha(0.6))
      .on('pointerup',   () => this.promptLeaveMatch());
  }

  private promptLeaveMatch(): void {
    // Delegate confirmation, API call, and navigation to React (GameScreen).
    // React listens for 'leave-match-request' and runs the shared forfeit flow,
    // which ensures every leave path calls the backend and updates DB state.
    window.dispatchEvent(new CustomEvent('leave-match-request'));
  }

  // ─── State Application ──────────────────────────────────

  /**
   * Single acceptance gate for authoritative game states. EVERY source
   * (REST fetch, initial_state, game_update, game_finished, reconnect
   * refresh, visibility-resume refresh) must pass here before
   * applyGameState. Returns false when the state is stale/duplicate —
   * the caller must then skip applyGameState (and, for REST paths, fall
   * back to _resyncPrivateUi so myPosition-dependent UI still updates).
   */
  private _gateGameState(gameState: GameState, source: string): boolean {
    const seq     = gameState.state_seq ?? null;
    const verdict = evaluateStateSeq(seq, this._lastAppliedStateSeq);
    if (GAME_DIAGNOSTICS) {
      diag.recordStateAcceptance({
        source,
        gameId:              this.gameId,
        sessionId:           this.sessionId ?? null,
        stateSeq:            seq,
        lastAppliedBefore:   this._lastAppliedStateSeq,
        accepted:            verdict.accepted,
        reason:              verdict.reason,
        turn:                gameState.current_turn,
        tileCount:           gameState.board_layout?.tiles?.length ?? 0,
        sceneInstanceId:     this._instanceId,
        subscriptionId:      `scene#${this._instanceId}:game=${this.gameId}`,
        visibility:          typeof document !== 'undefined' ? document.visibilityState : 'unknown',
        wsConnected:         this._wsConnected,
        firstRender:         this.board ? this.board.getTileSprites().size === 0 : true,
        animationsSuppressed: this.board ? this.board.getTileSprites().size === 0 : true,
      });
    }
    if (!verdict.accepted) {
      console.warn(
        `[GameScene#${this._instanceId}] rejected stale state  game=${this.gameId}` +
        ` source=${source} seq=${seq} lastApplied=${this._lastAppliedStateSeq}`,
      );
      return false;
    }
    this._lastAppliedStateSeq = verdict.nextLastApplied;
    return true;
  }

  /** Redraw only the player-card displays from a game state (used after a
   *  first-time flag-texture load; board/hand/score are untouched). */
  private refreshPlayerCards(gameState: GameState): void {
    if (this.myPosition < 0 && !this._isSpectator) return;
    if (this.isMobile && !this.useDesktopRender) {
      this.opponentHands?.update(
        gameState.players,
        gameState.hand_counts ?? {},
        gameState.current_turn,
        this.myPosition,
      );
    } else {
      this.desktopPlayerCards?.update(
        gameState.players,
        gameState.hand_counts ?? {},
        gameState.current_turn,
        this.myPosition,
      );
    }
  }

  private applyGameState(gameState: GameState): void {
    if (this._shutDown) {
      console.warn(`[GameScene#${this._instanceId}] applyGameState called after shutdown — ignored  game=${this.gameId}`);
      return;
    }
    console.log(`[GameScene#${this._instanceId}] applyGameState  game=${this.gameId} turn=${gameState.current_turn} myPos=${this.myPosition}`);
    if (GAME_DIAGNOSTICS) diag.recordApplyGameStateStart();

    // Detect new board tiles and set travel-animation sources for any that
    // were not already captured from the local hand (opponent / bot plays).
    if (gameState.board_layout?.tiles && this.currentGameState?.board_layout?.tiles) {
      const prevIds   = new Set(this.currentGameState.board_layout.tiles.map(t => t.id));
      const prevTurn  = this.currentGameState.current_turn;
      for (const t of gameState.board_layout.tiles) {
        if (!prevIds.has(t.id) && !this._travelSources.has(t.id)) {
          if (prevTurn !== undefined && prevTurn !== this.myPosition) {
            // Opponent or bot tile — use a directional origin near their card.
            this._travelSources.set(t.id, this._getOpponentTravelSource(prevTurn));
          } else if (this.myHand.some(h => h.id === t.id)) {
            // My own tile placed server-side (turn-timer auto-play): travel
            // from its rack slot instead of popping in.
            const rackPos = this.hand.getTileScreenPos(t.id);
            if (rackPos) this._travelSources.set(t.id, rackPos);
            if (GAME_DIAGNOSTICS) {
              diag.recordNoTravelSource({
                tileId: t.id, reason: 'auto-play-own-tile',
                prevTurn, myPos: this.myPosition,
              });
            }
          } else {
            // Batched/coalesced bot update: the backend can bundle bot turns
            // into one game_update where prevTurn has already cycled back to
            // this seat, so the actual mover is unknown. A generic opponent
            // origin still beats an instant no-animation pop-in.
            const seat = this.myPosition >= 0 ? (this.myPosition + 2) % 4 : 2;
            this._travelSources.set(t.id, this._getOpponentTravelSource(seat));
            if (GAME_DIAGNOSTICS) {
              diag.recordNoTravelSource({
                tileId: t.id, reason: 'batched-update-fallback',
                prevTurn, myPos: this.myPosition,
              });
            }
          }
        }
      }
    }

    this.currentGameState = gameState;

    // Preserve players for the round-end overlay — game_finished game_state may omit them.
    if (gameState.players?.length > 0) {
      this._lastKnownPlayers = gameState.players;
    }

    // Country Flag System V1: rasterize/cache the (≤4) distinct validated
    // flag textures this game needs. Textures persist in the game-level
    // cache, so round transitions and reconnects are no-ops; on a
    // first-time load, redraw the player cards once the textures arrive
    // (they rebuild from scratch on every update, so this is cheap).
    if (gameState.players?.length > 0) {
      ensureCountryFlagTextures(this, gameState.players, () => {
        if (this._shutDown || !this.currentGameState) return;
        this.refreshPlayerCards(this.currentGameState);
      });
      // Player Identity V1: same on-demand pattern for the (≤4) validated
      // avatar textures (human presets + stable bot assignments). Failed
      // loads never enter the cache — cards keep the initial fallback.
      ensureAvatarTextures(this, gameState.players, () => {
        if (this._shutDown || !this.currentGameState) return;
        this.refreshPlayerCards(this.currentGameState);
      });
      // Feed the React Players panel the sanitized authoritative identities.
      // Re-dispatched on every gated state so the panel survives updates,
      // round transitions, and reconnects.
      window.dispatchEvent(new CustomEvent('phaser:players-update', {
        detail: gameState.players.map(p => ({
          id:           p.id,
          display_name: p.display_name ?? '',
          country_code: p.country_code ?? null,
          avatar_key:   p.avatar_key ?? null,
          is_bot:       p.is_bot === true,
          bot_difficulty: p.bot_difficulty ?? null,
          presentation_role: p.presentation_role ?? null,
          is_me:        p.position === this.myPosition,
          team:         p.team,
          position:     p.position,
        })),
      }));
    }

    // Update board
    if (gameState.board_layout && gameState.board_layout.tiles) {
      this.renderBoardFromLayout(gameState.board_layout);

    }

    if (this.isMobile && !this.useDesktopRender) {
      this.scoreBoard?.update(gameState, this.myPosition, this.teamScores);
      // myPosition < 0 stays a no-op for a real participant whose position
      // hasn't resolved yet (unchanged) — a spectator's is permanently < 0,
      // so it needs the explicit _isSpectator escape hatch to ever render.
      if (this.myPosition >= 0 || this._isSpectator) {
        this.opponentHands?.update(
          gameState.players,
          gameState.hand_counts ?? {},
          gameState.current_turn,
          this.myPosition,
        );
      }
    } else {
      this.emitDesktopHudUpdate();
      if (this.myPosition >= 0 || this._isSpectator) {
        this.desktopPlayerCards?.update(
          gameState.players,
          gameState.hand_counts ?? {},
          gameState.current_turn,
          this.myPosition,
        );
      }
    }

    const isMyTurn = this.isMyTurn();
    console.log(`[GameScene#${this._instanceId}] applyGameState: turn=${gameState.current_turn}, myPos=${this.myPosition}, isMyTurn=${isMyTurn}`);

    if (isMyTurn && !this._prevIsMyTurn) {
      GameSoundManager.playTurnIndicator();
    }
    this._prevIsMyTurn = isMyTurn;

    // Reset ring timer whenever the turn changes.
    // Composite key: current_turn (seat index) + board tile count.
    // The tile count secondary key catches the case where the backend batches bot
    // turns server-side and sends one game_update with the same human seat index
    // as the previous turn — current_turn alone would not detect the change.
    const tilesNow    = gameState.board_layout?.tiles?.length ?? 0;
    const turnChanged = gameState.current_turn !== this._prevTurnPos ||
                        tilesNow !== this._prevTilesCount;

    if (turnChanged) {
      this._prevTurnPos    = gameState.current_turn;
      this._prevTilesCount = tilesNow;

      if (this._isActivePlayerBot(gameState.current_turn)) {
        // Bot's turn — hide the timer; bots play immediately, no countdown needed.
        this._turnTimer?.stop();
      } else {
        // Guard against NaN: new Date(invalidString).getTime() returns NaN, which
        // ?? does NOT catch (only null/undefined). NaN deadline → timer expires instantly.
        const rawMs = gameState.turn_deadline_at
          ? new Date(gameState.turn_deadline_at).getTime()
          : null;
        const deadlineMs = (rawMs !== null && !isNaN(rawMs)) ? rawMs : null;
        this._turnTimer?.reset(gameState.current_turn, this.myPosition, deadlineMs, this._isSpectator);
      }
    }


    this.hand.setMyTurn(isMyTurn);
    if (isMyTurn) {
      this.hand.enableInteraction();
      if (this.isMobile && !this.useDesktopRender) {
        this.scoreBoard?.showMessage('Your Turn!', 1500);
      }
    } else {
      this.hand.disableInteraction();
    }
  }

  private applyHand(hand: HandTile[]): void {
    if (this._shutDown) {
      console.warn(`[GameScene#${this._instanceId}] applyHand called after shutdown — ignored  game=${this.gameId}`);
      return;
    }
    console.log(`[GameScene#${this._instanceId}] applyHand  game=${this.gameId} tiles=${hand?.length ?? 0}`);

    // ── Deal animation is running ────────────────────────────────────────────
    // Update the pending hand so the reveal uses the freshest data, but do not
    // render the tiles yet.  Interaction stays locked until _revealPendingHand.
    if (this._pendingHandForReveal !== null) {
      this._pendingHandForReveal = hand ?? [];
      this.myHand = hand ?? [];
      this.hand.setMyTurn(this.isMyTurn());
      this.hand.disableInteraction();
      return;
    }

    if (!hand || hand.length === 0) {
      // Hand is empty (player just played their last tile) — still must sync
      // interaction state so we don't leave the UI locked or unlocked incorrectly.
      console.warn('[GameScene] applyHand called with empty/null hand');
    } else {
      // Trigger shuffle/deal only on a true new-game start:
      //   - board is still empty (no tiles played yet)
      //   - receiving a full starting hand of 7 for the first time this scene
      //   - haven't shown it yet (guard against WS duplicate messages)
      if (
        !this._hasShownDealAnimation &&
        hand.length === STARTING_HAND_SIZE &&
        this.myHand.length === 0 &&
        (this.currentGameState?.board_layout?.tiles?.length ?? 0) === 0
      ) {
        this._hasShownDealAnimation = true;
        this._pendingHandForReveal = hand;
        this.myHand = hand;

        // Safety fallback: reveal the hand if the animation somehow stalls.
        this._dealFallbackTimer = this.time.delayedCall(2000, () => {
          this._dealFallbackTimer = null;
          this._revealPendingHand();
        });

        this._runShuffleDeal(() => {
          this._dealFallbackTimer?.remove(false);
          this._dealFallbackTimer = null;
          this._revealPendingHand();
        });

        // Keep hand hidden and interaction locked during animation.
        this.hand.setMyTurn(this.isMyTurn());
        this.hand.disableInteraction();
        return;
      }

      this.myHand = hand;
      this.hand.setHand(hand);
    }

    // Always sync interaction regardless of hand size
    const myTurn = this.isMyTurn();
    this.hand.setMyTurn(myTurn);
    if (myTurn) {
      this.hand.enableInteraction();
    } else {
      this.hand.disableInteraction();
    }
  }

  /**
   * Minimum private-data sync performed when a REST hydration response is
   * discarded because WS already applied newer public state during the await.
   *
   * The board is already correct (WS rendered it). Only myPosition-dependent
   * UI elements need updating now that REST has provided myPosition.
   *
   * Intentionally does NOT call renderBoardFromLayout — the WS diff already
   * rendered all tile sprites; re-calling would re-run autoFitToPlayArea for
   * no visual benefit and could interfere with any in-flight ghost tweens.
   */
  private _resyncPrivateUi(): void {
    if (this._shutDown || !this.currentGameState) return;
    const gs = this.currentGameState;
    console.log(
      `[GameScene#${this._instanceId}] _resyncPrivateUi  game=${this.gameId}` +
      ` turn=${gs.current_turn} myPos=${this.myPosition}`,
    );

    // Scoreboard / HUD — now knows myPosition
    if (this.isMobile && !this.useDesktopRender) {
      this.scoreBoard?.update(gs, this.myPosition, this.teamScores);
      if (this.myPosition >= 0 || this._isSpectator) {
        this.opponentHands?.update(
          gs.players, gs.hand_counts ?? {}, gs.current_turn, this.myPosition,
        );
      }
    } else {
      this.emitDesktopHudUpdate();
      if (this.myPosition >= 0 || this._isSpectator) {
        this.desktopPlayerCards?.update(
          gs.players, gs.hand_counts ?? {}, gs.current_turn, this.myPosition,
        );
      }
    }

    // Turn indicator sound — fires once when myPosition is first known and it's our turn.
    // _prevIsMyTurn was false when WS initial_state fired (myPosition was -1 then).
    const isMyTurn = this.isMyTurn();
    if (isMyTurn && !this._prevIsMyTurn) {
      GameSoundManager.playTurnIndicator();
    }
    this._prevIsMyTurn = isMyTurn;

    // Timer re-anchor.
    // For setupNetwork: _prevTurnPos was reset to -1 by positionWasUnknown guard,
    // so turnChanged=true and the ring re-anchors with the correct myPosition.
    // For refreshGameState: _prevTurnPos was already updated by the WS applyGameState
    // that triggered the discard, so turnChanged=false and no duplicate reset fires.
    const tilesNow    = gs.board_layout?.tiles?.length ?? 0;
    const turnChanged = gs.current_turn !== this._prevTurnPos || tilesNow !== this._prevTilesCount;
    if (turnChanged) {
      this._prevTurnPos    = gs.current_turn;
      this._prevTilesCount = tilesNow;
      if (this._isActivePlayerBot(gs.current_turn)) {
        this._turnTimer?.stop();
      } else {
        const rawMs      = gs.turn_deadline_at ? new Date(gs.turn_deadline_at).getTime() : null;
        const deadlineMs = (rawMs !== null && !isNaN(rawMs)) ? rawMs : null;
        this._turnTimer?.reset(gs.current_turn, this.myPosition, deadlineMs, this._isSpectator);
      }
    }

    // Hand interaction state
    this.hand.setMyTurn(isMyTurn);
    if (isMyTurn) {
      this.hand.enableInteraction();
    } else {
      this.hand.disableInteraction();
    }
  }

  /** Render the deferred hand and restore normal interaction state. */
  private _revealPendingHand(): void {
    if (this._shutDown || this._pendingHandForReveal === null) return;
    const hand = this._pendingHandForReveal;
    this._pendingHandForReveal = null;

    if (hand.length > 0) {
      this.hand.setHand(hand);
    }

    const myTurn = this.isMyTurn();
    this.hand.setMyTurn(myTurn);
    if (myTurn) {
      this.hand.enableInteraction();
    } else {
      this.hand.disableInteraction();
    }
  }

  private renderBoardFromLayout(layout: BoardLayout): void {
    if (layout && layout.tiles) {
      if (GAME_DIAGNOSTICS) {
        diag.setRenderBoardStart(diag.currentTxnId);
        diag.recordBoardRenderStart();
      }
      this.board.renderBoard(layout.tiles, this._travelSources);
      if (GAME_DIAGNOSTICS) {
        diag.setRenderBoardEnd(diag.currentTxnId);
        diag.recordRenderStart();
      }
      this._travelSources.clear();
      // The board's travel ghost (if any) now exists at the drag-release point —
      // hand off from the held drag ghost in the same frame, no visual gap.
      this.hand.clearPendingPlayGhost();
      // Mobile only: auto-fit so the chain always fits the play area.
      // Desktop: table always stays full size — user can pan if chain overflows.
      if (this.mobilePlayAreaW > 0) {
        if (GAME_DIAGNOSTICS) {
          const before = { x: this.board.x, y: this.board.y, scaleX: this.board.scaleX, scaleY: this.board.scaleY };
          const t0 = performance.now();
          this.board.autoFitToPlayArea(
            this.mobilePlayAreaCX,
            this.mobilePlayAreaCY,
            this.mobilePlayAreaW,
            this.mobilePlayAreaH,
            0.76
          );
          const after = { x: this.board.x, y: this.board.y, scaleX: this.board.scaleX, scaleY: this.board.scaleY };
          diag.setAutoFitTimes(diag.currentTxnId, t0, performance.now(), before, after);
        } else {
          this.board.autoFitToPlayArea(
            this.mobilePlayAreaCX,
            this.mobilePlayAreaCY,
            this.mobilePlayAreaW,
            this.mobilePlayAreaH,
            0.76   // minimum scale — tiles never shrink below 76% of natural size
          );
        }
      }
      // Board transform is now final: retarget/complete any optimistic flight
      // (inert when the destination did not move), then start deferred travel
      // animations.
      this.board.reconcileOptimisticTravel();
      this.board.flushPendingTravelAnimations();
    }
  }

  /**
   * Returns a scene-space origin for the travel animation of an opponent tile.
   * Maps the opponent's seat to a directional source near their player card.
   */
  private _getOpponentTravelSource(seatPos: number): { x: number; y: number } {
    // Spectating: myPosition is permanently -1, so subtracting it directly
    // (as for a real participant) rotates every seat by one position — the
    // exact "tile flies in from the wrong side" bug. Anchor at seat 0
    // instead, same fixed convention as DesktopPlayerCards / OpponentHands /
    // PlayerTimerRings, so the travel origin matches wherever that seat's
    // card actually renders.
    const anchor  = this.myPosition >= 0 ? this.myPosition : 0;
    const relSeat = ((seatPos - anchor) + 4) % 4;
    const bx = this.board?.x ?? this.scale.width  / 2;
    const by = this.board?.y ?? this.scale.height / 2;
    const reach = 180;
    switch (relSeat) {
      case 1: return { x: bx + reach, y: by };         // right card
      case 2: return { x: bx,         y: by - reach }; // top (partner)
      case 3: return { x: bx - reach, y: by };         // left card
      default:
        // relSeat 0 = anchor's own seat. For a real participant this is
        // literally "me" and should never be reached — their own tile plays
        // travel from the hand rack (a different code path), never this
        // function. For a spectator, anchor seat 0 is a REAL other player
        // now rendered at the mirrored bottom panel (see
        // DesktopPlayerCards.panelBottom) — mirror 'top' across the board
        // center so the travel origin matches where that card actually sits.
        return this._isSpectator ? { x: bx, y: by + reach } : { x: bx, y: by };
    }
  }

  // ─── Player Actions ─────────────────────────────────────

  private async handlePlayTile(tileId: number, position: 'left' | 'right', sourcePos?: { x: number; y: number }, chosenVp?: ValidPosition, inputMethod?: 'click' | 'drag'): Promise<void> {
    if (this._shutDown || this.currentGameState?.status !== 'active') {
      console.warn('[GameScene] ignoring playTile — game not active or shutting down');
      return;
    }
    if (!this.isMyTurn()) {
      console.warn('[GameScene] Not my turn');
      return;
    }

    // Prefer drag release point when provided (drag-to-play); fall back to the
    // rack tile position for tap-to-play. Must be captured before disableInteraction
    // re-renders the rack and moves the sprite.
    const handPos = sourcePos ?? this.hand.getTileScreenPos(tileId);
    if (handPos) this._travelSources.set(tileId, handPos);

    this.hand.disableInteraction();

    // ── Optimistic travel (desktop and mobile) ───────────────────────────────
    // The ghost launches in the same frame as release/tap toward the
    // destination derived from the server-precomputed valid_position under the
    // CURRENT board transform. On mobile that destination is provisional —
    // autoFitToPlayArea may change the transform when the authoritative update
    // renders — so renderBoardFromLayout calls board.reconcileOptimisticTravel()
    // after autoFit to retarget the flight. engine_failed moves without
    // coordinates keep the server-confirmed flow.
    if (chosenVp && handPos &&
        typeof chosenVp.x === 'number' && typeof chosenVp.y === 'number') {
      const tileData = this.myHand.find(t => t.id === tileId);
      if (tileData) {
        const started = this.board.startOptimisticTravel(
          tileData, chosenVp, handPos,
          // Timer-race supersede: the server placed a different move — fly
          // the ghost home and re-show the rack sprite when it arrives.
          () => {
            const rackPos = this.hand.getTileScreenPos(tileId);
            this.board.cancelOptimisticTravel(
              rackPos ?? undefined,
              () => this.hand.showRackTile(tileId),
            );
          },
        );
        // The optimistic ghost replaces the held drag ghost in the same
        // frame — also bypasses the mobile release-acknowledgement animation.
        // For click plays the rack sprite is still visible at commit time, so
        // hide it too (idempotent for drag — already hidden in _commitDrag).
        if (started) {
          this.hand.clearPendingPlayGhost();
          this.hand.hideRackTile(tileId);
        }
      }
    }

    try {
      if (GAME_DIAGNOSTICS) diag.recordRequestSent();
      await this.transport.playTile(this.gameId, tileId, position, { viaDrag: inputMethod === 'drag' });
      if (GAME_DIAGNOSTICS) diag.recordRestResponse();
      console.log('[GameScene] playTile REST success — waiting for WS game_update');
      // Do NOT apply the REST response here. WS game_update / private_update are
      // authoritative and may arrive before this response returns, so applying
      // REST state here would rewind current_turn and lock the UI again.
    } catch (err) {
      console.error('[GameScene] playTile REST failed:', err);
      // Play rejected — drop the held drag ghost; the tile stays in the rack.
      this.hand.clearPendingPlayGhost();
      // Desktop optimistic flight: roll the ghost back to its rack slot and
      // re-show the rack sprite on arrival. No-op when no flight is active
      // (mobile / click flow), preserving current behavior exactly.
      const rackPos = this.hand.getTileScreenPos(tileId);
      this.board.cancelOptimisticTravel(
        rackPos ?? undefined,
        () => this.hand.showRackTile(tileId),
      );
      if (this.isMyTurn()) {
        this.hand.enableInteraction();
      }
    }
  }

  private async handlePassTurn(): Promise<void> {
    if (this._shutDown || this.currentGameState?.status !== 'active') {
      console.warn('[GameScene] ignoring passTurn — game not active or shutting down');
      return;
    }
    if (!this.isMyTurn()) {
      console.warn('[GameScene] Not my turn');
      return;
    }

    console.log('[GameScene] Passing turn');
    this.hand.disableInteraction();
    GameSoundManager.playPass();

    try {
      await this.transport.passTurn(this.gameId);
      console.log('[GameScene] passTurn REST success — waiting for WS game_update');
      // Same reasoning as handlePlayTile: let WS be the sole state authority.
    } catch (err) {
      console.error('[GameScene] passTurn REST failed:', err);
      if (this.isMyTurn()) {
        this.hand.enableInteraction();
      }
    }
  }

  // ─── Helpers ─────────────────────────────────────────────

  /** Show or hide the KnockButton based on pass availability + game state. */
  private _syncKnock(passAvailable: boolean): void {
    // Arm the PASS button only when a pass is actually required; otherwise it
    // reverts to its inactive face (desktop: still visible; mobile: hidden).
    this._knockButton?.setArmed(
      passAvailable && this.currentGameState?.status === 'active',
    );
  }

  /** Maps computeLayout output to the KnockButtonLayout interface. */
  private _knockLayout(layout: ReturnType<typeof this.computeLayout>): KnockButtonLayout {
    const base: KnockButtonLayout = {
      screenW:    layout.screenW,
      handY:      layout.handY,
      handScale:  layout.handScale,
      sidePanelW: layout.sidePanelW,
      unit:       layout.unit,
    };
    // Desktop: exact PASS position/size from the layout spec.
    if (layout.layoutK > 0) {
      const k = layout.layoutK;
      const C = DESIGN_SIZE / 2;
      base.anchorX  = designToCanvas(LAYOUT_SPEC.pass.x, C, layout.boardX, k);
      base.anchorY  = designToCanvas(LAYOUT_SPEC.pass.y, C, layout.boardY, k);
      base.iconSize = Math.round(LAYOUT_SPEC.pass.size * k);
    }
    return base;
  }

  /** Maps computeLayout output to the PlayerTimerRingsLayout interface. */
  private _timerLayout(layout: ReturnType<typeof this.computeLayout>): PlayerTimerRingsLayout {
    return {
      screenW:         layout.screenW,
      screenH:         layout.screenH,
      sidePanelW:      layout.sidePanelW,
      topPanelH:       layout.topPanelH,
      playAreaTopY:    layout.playAreaTopY,
      playAreaBottomY: layout.playAreaBottomY,
      boardX:          layout.boardX,
      boardY:          layout.boardY,
      boardVisualSize: layout.boardVisualSize,
      unit:            layout.unit,
      handY:           layout.handY,
      isSpectator:     this._isSpectator,
    };
  }

  /** Pass board geometry to OpponentHands so it can position its panels. */
  private applyOpponentHandsLayout(layout: ReturnType<typeof this.computeLayout>): void {
    this._turnTimer?.setLayout(this._timerLayout(layout));
    this._knockButton?.setLayout(this._knockLayout(layout));
    this.opponentHands?.setLayout({
      boardX:          layout.boardX,
      boardY:          layout.boardY,
      boardVisualSize: layout.boardVisualSize,
      unit:            layout.unit,
      isMobile:        layout.isMobile,
      screenW:         layout.screenW,
      screenH:         layout.screenH,
      playCX:          layout.boardX,
      playCY:          layout.boardY,
      playW:           layout.mobilePlayW,
      playH:           layout.mobilePlayH,
      sidePanelW:      layout.sidePanelW,
      topPanelH:       layout.topPanelH,
      playAreaTopY:    layout.playAreaTopY,
      playAreaBottomY: layout.playAreaBottomY,
    });
  }

  private isMyTurn(): boolean {
    if (!this.currentGameState || this.myPosition === -1) return false;
    return this.currentGameState.current_turn === this.myPosition;
  }

  /** Returns true if the player at the given seat position is a bot, per
   *  the payload's authoritative is_bot flag. Returns false if the seat has
   *  no player entry. (The old @domino.bot email check silently broke when
   *  P0 privacy removed emails from game payloads — never reintroduce it.
   *  This also restores turn-timer suppression during bot turns.) */
  private _isActivePlayerBot(seatPosition: number): boolean {
    const player = this.currentGameState?.players.find(p => p.position === seatPosition);
    return player?.is_bot === true;
  }

  private handleResize(width: number, height: number): void {
    if (this._shutDown) return;

    const prevUnit = GAME_DIAGNOSTICS ? getBoardUnitSize() : 0;

    const layout = this.computeLayout(width, height);
    this.isMobile = layout.isMobile;
    const isMobileRender = this.isMobile && !this.useDesktopRender;

    // 1. Update global unit size (Board, Hand, DominoTile all read this)
    setBoardUnitSize(layout.unit);

    if (GAME_DIAGNOSTICS) {
      const tileCount = this.currentGameState?.board_layout?.tiles?.length ?? 0;
      diag.beginTransaction('resize', this.gameId, this.currentGameState?.game_number,
        this.currentGameState?.current_turn ?? -1, tileCount, layout.unit, performance.now(), prevUnit);
      diag.flagResizeDuringTransaction(diag.currentTxnId);
      // Check each existing tile for stale local position vs new unit size
      if (this.currentGameState?.board_layout?.tiles) {
        const sprites = this.board.getTileSprites();
        for (const td of this.currentGameState.board_layout.tiles) {
          const sp = sprites.get(td.id);
          if (sp) diag.checkTileLocalPosition(td.id, sp.x, sp.y, layout.unit);
        }
      }
    }

    // 1b. Sync background color with mobile/desktop mode
    this.cameras.main.setBackgroundColor(isMobileRender ? '#162b0a' : DESKTOP_THEME.color.pageBg);
    this.drawVignette(width, height, isMobileRender);
    this.drawLayoutFurniture(layout);

    // Refresh stored play area dimensions
    if (isMobileRender) {
      this.mobilePlayAreaCX = layout.boardX;
      this.mobilePlayAreaCY = layout.boardY;
      this.mobilePlayAreaW  = layout.mobilePlayW;
      this.mobilePlayAreaH  = layout.mobilePlayH;
    } else {
      this.desktopFeltCX   = layout.desktopFeltCX;
      this.desktopFeltCY   = layout.desktopFeltCY;
      this.desktopFeltSize = layout.desktopFeltSize;
    }

    // 2. Redraw board felt at new unit size, then reposition
    this.board.setMobileMode(isMobileRender);
    this.board.fitToZone();

    // 2a. Snap any in-flight travel tween before moving the container.
    //     tween.stop() does NOT call onComplete in Phaser 3 — this is safe.
    const _snappedTileId = this.board.snapActiveTravelTween();
    if (GAME_DIAGNOSTICS && _snappedTileId !== null) {
      diag.notifyTravelSnapped(_snappedTileId);
    }

    this.board.setPosition(layout.boardX, layout.boardY);

    // 2b. Reproject all existing placed tiles from canonical state at the new unit.
    //     Must run AFTER setBoardUnitSize (step 1) and setPosition, BEFORE renderBoard.
    const _canonicalTiles = this.currentGameState?.board_layout?.tiles ?? [];
    const _reprojected    = this.board.reprojectPlacedTiles(_canonicalTiles);

    if (GAME_DIAGNOSTICS) {
      diag.recordResizeReproject(diag.currentTxnId, {
        viewportW:    width,
        viewportH:    height,
        boardX:       layout.boardX,
        boardY:       layout.boardY,
        reprojected:  _reprojected,
        travelSnapped: _snappedTileId !== null,
        snappedTileId: _snappedTileId ?? undefined,
      });
      if (_canonicalTiles.length > 0) {
        diag.checkResizeInvariant(
          diag.currentTxnId,
          _canonicalTiles,
          this.board.getTileSprites(),
          layout.unit,
        );
      }
    }

    // 3. Diff-render board tiles — adds new tiles, removes absent ones.
    //    Existing tile positions are already correct from reprojectPlacedTiles above.
    if (this.currentGameState?.board_layout?.tiles) {
      this.board.renderBoard(this.currentGameState.board_layout.tiles);
      if (this.mobilePlayAreaW > 0) {
        if (GAME_DIAGNOSTICS) {
          const before = { x: this.board.x, y: this.board.y, scaleX: this.board.scaleX, scaleY: this.board.scaleY };
          const t0 = performance.now();
          this.board.autoFitToPlayArea(
            this.mobilePlayAreaCX, this.mobilePlayAreaCY, this.mobilePlayAreaW, this.mobilePlayAreaH, 0.76
          );
          const after = { x: this.board.x, y: this.board.y, scaleX: this.board.scaleX, scaleY: this.board.scaleY };
          diag.setAutoFitTimes(diag.currentTxnId, t0, performance.now(), before, after);
        } else {
          this.board.autoFitToPlayArea(
            this.mobilePlayAreaCX,
            this.mobilePlayAreaCY,
            this.mobilePlayAreaW,
            this.mobilePlayAreaH,
            0.76
          );
        }
      }
    }
    // Resize/orientation change may have moved the board transform and/or the
    // unit size — retarget any in-flight optimistic ghost to the recomputed
    // destination rather than letting it land at a stale point.
    this.board.reconcileOptimisticTravel();

    // 4. Re-render hand at new unit size, scale, and anchor
    this.hand.setMobile(this.isMobile);
    this.hand.setHandScale(layout.handScale);
    this.hand.setYAnchor(layout.handY);
    this.hand.setSlotSpacing(layout.handSlotSpacing > 0 ? layout.handSlotSpacing : null);
    this.hand.setRackCardVisible(layout.layoutK <= 0);
    this.hand.handleResize();

    if (isMobileRender) {
      // 5. Rebuild scoreboard for new width
      this.scoreBoard?.handleResize(width);
      if (this.currentGameState) {
        this.scoreBoard?.update(this.currentGameState, this.myPosition, this.teamScores);
      }

      // 6. Reposition opponent hand panels for new layout
      this.applyOpponentHandsLayout(layout);
      if (this.currentGameState && (this.myPosition >= 0 || this._isSpectator)) {
        this.opponentHands?.update(
          this.currentGameState.players,
          this.currentGameState.hand_counts ?? {},
          this.currentGameState.current_turn,
          this.myPosition,
        );
      }
    } else {
      this.emitDesktopLayoutUpdate(layout.boardX, layout.boardY, layout.boardVisualSize);
      this.desktopPlayerCards?.setLayout(layout.boardX, layout.boardY, layout.boardVisualSize, layout.unit, layout.layoutK);
      if (this.currentGameState) {
        this.emitDesktopHudUpdate();
        if (this.myPosition >= 0 || this._isSpectator) {
          this.desktopPlayerCards?.update(
            this.currentGameState.players,
            this.currentGameState.hand_counts ?? {},
            this.currentGameState.current_turn,
            this.myPosition,
          );
        }
      }
    }

    // 7. Reposition ring timers + knock button for new canvas size (desktop path —
    //    mobile is handled inside applyOpponentHandsLayout above)
    if (!isMobileRender) {
      this._turnTimer?.setLayout(this._timerLayout(layout));
      this._knockButton?.setLayout(this._knockLayout(layout));
    }

    if (GAME_DIAGNOSTICS) diag.endTransaction(diag.currentTxnId);
  }

  /**
   * Layout-spec furniture: the hand rack holder image and the team-colored
   * glow behind the local hand zone, positioned per config/layoutSpec.ts.
   * Desktop only; rebuilt on create and on every resize. Depths sit below
   * Hand's rack layer (44/45) so hand tiles and the turn glow stay on top,
   * preserving the spec's stacking (holder < hand bg < tiles).
   */
  private drawLayoutFurniture(layout: ReturnType<typeof this.computeLayout>): void {
    this._holderImg?.destroy();
    this._holderImg = null;
    this._bgImg?.destroy();
    this._bgImg = null;

    const k = layout.layoutK;
    if (k <= 0) return; // mobile — spec furniture is desktop-only for now

    const cx = layout.boardX;
    const cy = layout.boardY;
    const C  = DESIGN_SIZE / 2;

    // Wood-plank backdrop (bg_00). Sits behind the table (depth -5, above the
    // black camera clear); the table's transparent rounded corners reveal it
    // at the square's corners. Sized just past the table so the corners are
    // fully covered (design backdrop 1035 vs table 1000 → ~1.05x).
    if (this.textures.exists('game-bg')) {
      this._bgImg = this.add.image(cx, cy, 'game-bg');
      const bgSize = layout.boardVisualSize * 1.05;
      this._bgImg.setDisplaySize(bgSize, bgSize);
      this._bgImg.setDepth(-5);
    }

    if (this.textures.exists('tile-holder')) {
      const s = LAYOUT_SPEC.holder;
      this._holderImg = this.add.image(
        designToCanvas(s.x, C, cx, k), designToCanvas(s.y, C, cy, k), 'tile-holder',
      );
      this._holderImg.setDisplaySize(s.w * k, s.h * k);
      this._holderImg.setDepth(40);
    }

    // The red hand background (team-a-bg) is now owned by Hand.ts so it can
    // HUG the live tile count and shrink as tiles are played, instead of
    // sitting at a fixed 7-tile size here.
  }

  private drawVignette(w: number, h: number, isMobile: boolean): void {
    if (this.vignetteGfx) {
      this.vignetteGfx.destroy();
      this.vignetteGfx = null;
    }
    if (!isMobile) return;

    const gfx = this.add.graphics();
    gfx.setDepth(2);   // above felt background, below all game objects

    const strip = 90;  // vignette gradient width in px

    // Top edge: dark at screen edge → transparent
    gfx.fillGradientStyle(0x000000, 0x000000, 0x000000, 0x000000, 0.32, 0.32, 0, 0);
    gfx.fillRect(0, 0, w, strip);

    // Bottom edge: transparent → dark
    gfx.fillGradientStyle(0x000000, 0x000000, 0x000000, 0x000000, 0, 0, 0.32, 0.32);
    gfx.fillRect(0, h - strip, w, strip);

    // Left edge: dark → transparent
    gfx.fillGradientStyle(0x000000, 0x000000, 0x000000, 0x000000, 0.28, 0, 0.28, 0);
    gfx.fillRect(0, 0, strip, h);

    // Right edge: transparent → dark
    gfx.fillGradientStyle(0x000000, 0x000000, 0x000000, 0x000000, 0, 0.28, 0, 0.28);
    gfx.fillRect(w - strip, 0, strip, h);

    this.vignetteGfx = gfx;
  }

  private emitDesktopLayoutUpdate(boardX: number, boardY: number, tableSize: number): void {
    window.dispatchEvent(new CustomEvent('game-layout-update', {
      detail: { boardX, boardY, tableSize, canvasH: this.scale.height },
    }));
  }

  private emitDesktopHudUpdate(): void {
    if (!this.currentGameState) return;
    const gs      = this.currentGameState;
    const myTeam  = this.myTeam;
    // Spectating: myTeam is always -1 (no seat of their own — same sentinel
    // as myPosition), so "us"/"them" have no meaning. Show team 0 ("Team A"
    // in MatchCard/PlayersCard) on the left and team 1 ("Team B") on the
    // right instead of silently freezing both at 0 — same anchor=0
    // convention as the seating layout, and it lines up with the real Team
    // A / Team B labels regardless of anchor since those ARE teams 0 and 1.
    const usScore   = myTeam >= 0 ? (this.teamScores[String(myTeam)]     ?? 0)
                     : this._isSpectator ? (this.teamScores['0'] ?? 0) : 0;
    const themScore = myTeam >= 0 ? (this.teamScores[String(1 - myTeam)] ?? 0)
                     : this._isSpectator ? (this.teamScores['1'] ?? 0) : 0;
    window.dispatchEvent(new CustomEvent('game-hud-update', {
      detail: {
        usScore,
        themScore,
        isMyTurn:    this.isMyTurn(),
        currentTurn: gs.current_turn,
        players: gs.players.map(p => ({
          position: p.position,
          name:     getPlayerName(p, `P${p.position}`),
          team:     p.team,
        })),
        handCounts:  gs.hand_counts ?? {},
        myPosition:  this.myPosition,
      },
    }));
  }

  // ─── Shuffle/Deal Animation ──────────────────────────────

  private _runShuffleDeal(onComplete: () => void): void {
    const cx = this.board?.x ?? this.scale.width  / 2;
    const cy = this.board?.y ?? this.scale.height / 2;
    const w  = this.scale.width;
    const h  = this.scale.height;

    // Tile display size: match a horizontal board tile at current unit.
    const unit  = getBoardUnitSize();
    const tileW = unit * 2;
    const tileH = unit;

    // Target areas for the four relative player positions.
    // Margins are intentionally generous so the fakes clearly fly off-centre.
    const margin = Math.round(Math.min(w, h) * 0.06);
    const targets = {
      self:  { x: cx,          y: h - margin },
      right: { x: w - margin,  y: cy },
      top:   { x: cx,          y: margin },
      left:  { x: margin,      y: cy },
    };

    this._shuffleDealAnimator?.destroy();
    this._shuffleDealAnimator = new ShuffleDealAnimator(
      this, cx, cy, tileW, tileH, targets,
      () => {
        this._shuffleDealAnimator = null;
        onComplete();
      },
      this.viewerTileSkin,
    );
  }

  /** Brief scale pulse on the board container — visual punctuation for game end. */
  private _pulseBoard(): void {
    if (!this.board || this._shutDown) return;
    this.tweens.add({
      targets:  this.board,
      scaleX:   1.04,
      scaleY:   1.04,
      duration: 180,
      ease:     'Sine.easeOut',
      yoyo:     true,
      hold:     60,
    });
  }

  private cleanup(): void {
    console.log(`[GameScene#${this._instanceId}] cleanup  game=${this.gameId} alreadyShutDown=${this._shutDown}`);
    if (this._shutDown) return;   // idempotent — shutdown() and destroy() both call this
    this._shutDown = true;
    this._unsubscribeLocale?.();
    this._unsubscribeLocale = null;
    if (GAME_DIAGNOSTICS) diag.detach();
    if (this._onVisibilityChange) {
      document.removeEventListener('visibilitychange', this._onVisibilityChange);
      this._onVisibilityChange = null;
    }
    this.board?.cancelOptimisticTravel();
    this.board?.clearTravelState();
    GameSoundManager.unregisterPhaserSound(this.sound);
    this.scale?.off('resize', this._onScaleResize, this);
    this._knockButton?.destroy();
    this._knockButton = null;
    this._turnTimer?.stop();
    this._turnTimer = null;
    this._tutorialBoundsDetach?.();
    this._tutorialBoundsDetach = null;
    this._leaveMatchBtn?.destroy();
    this._leaveMatchBtn = null;
    this._dealFallbackTimer?.remove(false);
    this._dealFallbackTimer = null;
    this._pendingHandForReveal = null;
    this._shuffleDealAnimator?.destroy();
    this._shuffleDealAnimator = null;
    this.gameChannel?.unsubscribe();
    this._sessionHandle?.release();
    this._sessionHandle = null;
    if (this._onNavigateHome) {
      window.removeEventListener('phaser:navigate-home', this._onNavigateHome);
      this._onNavigateHome = null;
    }
    if (this._onRematchSnapshot) {
      window.removeEventListener('phaser:rematch-snapshot', this._onRematchSnapshot);
      this._onRematchSnapshot = null;
    }
    // Exit handshake: a torn-down scene must never transition later. The
    // running-transition path is safe — the handshake consumed its pending
    // entry before invoking _startNextGame, so this cancel is a no-op there.
    this._nextRoundHandshake.cancel();
    if (this._onRoundEndExitedAck) {
      window.removeEventListener('react:round-end-exited', this._onRoundEndExitedAck);
      this._onRoundEndExitedAck = null;
    }
    console.log(`[GameScene#${this._instanceId}] GameChannel + SessionChannel unsubscribed  game=${this.gameId}`);
    this.hand?.destroy();
    console.log(`[GameScene#${this._instanceId}] Hand destroyed  game=${this.gameId}`);
  }

  // Called by Phaser when scene.start() switches to another scene (scene-switch path).
  shutdown(): void {
    console.log(`[GameScene#${this._instanceId}] shutdown  game=${this.gameId}`);
    this.cleanup();
  }
}
